import { AfterViewChecked, ChangeDetectorRef, Component, inject, NgZone, OnDestroy, OnInit, QueryList, signal, ViewChildren } from '@angular/core';

import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { ActivatedRoute, Router } from '@angular/router';
import { DateTime } from 'luxon';
import { DecimalPipe, NgTemplateOutlet } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { cloneDeep } from 'lodash-es';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { v4 as uuidv4 } from 'uuid';
import { EnfafButtonComponent } from '../../../components/enfaf-button/enfaf-button.component';
import { EnfafCheckboxComponent } from '../../../components/enfaf-checkbox/enfaf-checkbox.component';
import { EnfafIconComponent } from '../../../components/enfaf-icon/enfaf-icon.component';
import { EnfafInputFileDraggerDarkComponent } from '../../../components/enfaf-input-file-dragger-dark/enfaf-input-file-dragger-dark.component';
import { EnfafInputSingleFileDraggerDarkComponent } from '../../../components/enfaf-input-single-file-dragger-dark/enfaf-input-single-file-dragger-dark.component';
import { EnfafInputComponent } from '../../../components/enfaf-input/enfaf-input.component';
import { EnfafSelectComponent, EnfafSelectOption } from '../../../components/enfaf-select/enfaf-select.component';
import { EnfafSwitchDarkComponent } from '../../../components/enfaf-switch-dark/enfaf-switch-dark.component';
import { ClassMessageConfigComponent } from '../../../components/class-message-config/class-message-config.component';
import { EnfafTextareaDarkComponent } from '../../../components/enfaf-textarea-dark/enfaf-textarea-dark.component';
import { AlertService } from '../../../core/services/alert/alert.service';
import { AuthService } from '../../../core/services/auth/auth.service';
import { ApiService } from '../../../core/services/api/api.service';
import { ToastService } from '../../../core/services/toast/toast.service';
import { ModalService } from '../../../core/services/modal/modal.service';
import { extractVimeoIdFromClipboardEvent, extractVimeoVideoId, getVimeoEmbedUrl } from '../../../core/utils/vimeo-url.util';
import { ClassToEdit } from '../../../models/classes/courses/class_to_edit.model';
import { ModuleToEdit } from '../../../models/classes/courses/module_to_edit.model';
import { SectionToEdit } from '../../../models/classes/courses/section_to_edit.model';
import { MessageDto } from '../../../models/dto/message.dto';
import { MessageSendCondition } from '../../../models/enums/message_send_condition.enum';
import { CourseToEdit } from '../../../models/interfaces/course_to_edit.interface';
import { CourseYear } from '../../../models/interfaces/course_year.interface';
import { CustomFile, CustomUploadedFile } from '../../../models/interfaces/custom_file.interface';
import { UtilsService } from '../../../shared/utils/utils.service';
import { sortSectionsByYear } from '../../../shared/utils/sort-sections-by-year.util';
import { EnfafDatetimePickerLightComponent } from '../../../components/enfaf-datetime-picker-light/enfaf-datetime-picker-light.component';
import { AdminLessonNotesComponent } from './admin-lesson-notes/admin-lesson-notes.component';
import { AdminQuizQuestionsComponent } from './admin-quiz-questions/admin-quiz-questions.component';
import { AdminClassZeroComponent } from './admin-class-zero/admin-class-zero.component';
import { BulkNotesGenerationModalComponent } from './bulk-notes-generation-modal/bulk-notes-generation-modal.component';
import { BulkQuizGenerationModalComponent } from './bulk-quiz-generation-modal/bulk-quiz-generation-modal.component';
import { AdminTfmConfigComponent } from './admin-tfm-config/admin-tfm-config.component';
import { SocketService } from '../../../core/services/socket/socket.service';
import { SocketEvent } from '../../../models/enums/socket_event.enum';
import { BulkNotesProgressEvent, BulkNotesCompleteEvent, BulkQuizProgressEvent, BulkQuizCompleteEvent } from '../../../core/services/api/api.service';
import { SettingsService } from '../../../core/services/settings/settings.service';

@Component({
  selector: 'app-course-content',
  imports: [
    DecimalPipe,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatCheckboxModule,
    MatButtonModule,
    MatSlideToggleModule,
    MatExpansionModule,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    EnfafInputComponent,
    EnfafButtonComponent,
    EnfafCheckboxComponent,
    EnfafIconComponent,
    EnfafInputFileDraggerDarkComponent,
    EnfafInputSingleFileDraggerDarkComponent,
    EnfafTextareaDarkComponent,
    EnfafSwitchDarkComponent,
    EnfafSelectComponent,
    ClassMessageConfigComponent,
    MatTooltip,
    AdminLessonNotesComponent,
    BulkNotesGenerationModalComponent,
    EnfafDatetimePickerLightComponent,
    AdminQuizQuestionsComponent,
    BulkQuizGenerationModalComponent,
    AdminTfmConfigComponent,
    AdminClassZeroComponent
  ],
  templateUrl: './course-content.component.html',
  styleUrl: './course-content.component.scss'
})
export class CourseContentComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChildren(AdminLessonNotesComponent) notesComponents!: QueryList<AdminLessonNotesComponent>;
  @ViewChildren(AdminQuizQuestionsComponent) quizComponents!: QueryList<AdminQuizQuestionsComponent>;
  @ViewChildren(AdminClassZeroComponent) classZeroComponents!: QueryList<AdminClassZeroComponent>;

  courseToEdit!: CourseToEdit;
  courseForm!: FormGroup;
  isSavingCourse = false;
  isLoadingCourse = true;
  isLoadingAttachments = false;
  hasUnsavedNotes = false;
  hasUnsavedQuizzes = false;
  hasUnsavedClassZero = false;
  processingClassIds = new Set<string>();
  courseId: string | null = null;
  isBulkModalVisible = false;
  isBulkQuizModalVisible = false;
  isBulkGenerating = false;
  isBulkQuizGenerating = false;
  quizFeatureEnabled = signal(false);
  bulkBannerState: 'idle' | 'generating' | 'done' = 'idle';
  bulkCompletedCount = 0;
  bulkTotalCount = 0;
  bulkSuccessCount = 0;
  bulkErrorCount = 0;
  bulkQuizBannerState: 'idle' | 'generating' | 'done' = 'idle';
  bulkQuizCompletedCount = 0;
  bulkQuizTotalCount = 0;
  bulkQuizSuccessCount = 0;
  bulkQuizErrorCount = 0;
  courseName: string = '';
  courseRequiresTfm = false;
  showTfmSection = false;

  // Calendar image properties
  calendarLocalFile: CustomFile | null = null;
  calendarCurrentFile: CustomUploadedFile | null = null;
  calendarImageAction: 'keep' | 'delete' = 'keep';
  isCalendarPreviewOpen = false;

  today = new Date();
  expandedSections: boolean[] = [];
  expandedModules: boolean[][] = [];
  expandedClasses: boolean[][][] = [];
  /** FP courses only — one entry per courseToEdit.years, drives the collapsible "Año" panel that wraps its asignaturas. */
  expandedYears: boolean[] = [];
  fetchedUrls: {
    sections: Array<boolean>;
    classes: Array<Array<Array<boolean>>>;
  } = { sections: [], classes: [] };
  currentAttachments: {
    sections: Array<{
      attachments: Array<CustomUploadedFile>;
      modules: Array<{
        classes: Array<{
          attachments: Array<CustomUploadedFile>;
        }>;
      }>;
    }>;
  } = { sections: [] };

  messages: MessageDto[] = [];
  messageOptions: EnfafSelectOption[] = [];
  // Twilio template cache — replaced by Kommo bot info
  // loadedTemplates: Map<string, string> = new Map();

  sectionIndex: number = 0;
  moduleIndex: number = 0;
  classIndex: number = 0;
  targetClassId: string | null = null;

  movingClassForm = this.fb.group({
    classId: this.fb.control<string | null>(null, Validators.required),
    moduleId: this.fb.control<string | null>(null, Validators.required),
    sectionId: this.fb.control<string | null>(null, Validators.required)
  });

  movingModuleForm = this.fb.group({
    moduleId: this.fb.control<string | null>(null, Validators.required),
    sectionId: this.fb.control<string | null>(null, Validators.required)
  });

  // Estado del último movimiento de clase para permitir deshacer
  private lastClassMove: {
    class: ClassToEdit;
    classFormGroup: FormGroup;
    fromSectionIndex: number;
    fromModuleIndex: number;
    fromClassIndex: number;
    toSectionIndex: number;
    toModuleIndex: number;
    attachments: any;
    expansionState: boolean;
  } | null = null;

  // Estado del último movimiento de módulo para permitir deshacer
  private lastModuleMove: {
    module: ModuleToEdit;
    moduleFormGroup: FormGroup;
    fromSectionIndex: number;
    fromModuleIndex: number;
    toSectionIndex: number;
    attachments: any;
    expansionStates: {
      moduleExpanded: boolean;
      classesExpanded: boolean[];
    };
  } | null = null;

  get movingClassId(): string | null {
    return this.movingClassForm.get('classId')?.value || null;
  }

  get movingModuleId(): string | null {
    return this.movingModuleForm.get('moduleId')?.value || null;
  }

  get movingSectionId(): string | null {
    return this.movingClassForm.get('sectionId')?.value || null;
  }

  /**
   * Section label for the "move" selectors — appends the academic year for FP
   * courses (ENF-2282 / US B.2: moving to a section of another year is already
   * supported since these selectors already list every section of the course
   * regardless of year; the only change needed is making the year visible).
   */
  private sectionMoveLabel(section: SectionToEdit): string {
    const yearNumber = section.courseYearId ? this.getYearNumber(section.courseYearId) : null;
    return yearNumber ? `${section.name} (Año ${yearNumber})` : section.name;
  }

  get sectionClassOptions(): EnfafSelectOption[] {
    return (
      this.courseToEdit?.sections?.map((section) => ({
        value: section.courseSectionId,
        viewValue: this.sectionMoveLabel(section)
      })) ?? []
    );
  }

  get sectionModuleOptions(): EnfafSelectOption[] {
    return (
      this.courseToEdit?.sections?.map((section) => ({
        value: section.courseSectionId,
        viewValue: this.sectionMoveLabel(section)
      })) ?? []
    );
  }

  get moduleClassOptions(): EnfafSelectOption[] {
    if (!this.movingSectionId) {
      return (
        this.courseToEdit?.sections?.flatMap((section) =>
          section.modules.map((module) => ({
            value: module.courseModuleId,
            viewValue: `${module.name} (Sección: ${this.sectionMoveLabel(section)})`
          }))
        ) ?? []
      );
    }

    // Filter modules by the selected section
    const selectedSection = this.courseToEdit?.sections?.find((section) => section.courseSectionId === this.movingSectionId);

    return (
      selectedSection?.modules?.map((module) => ({
        value: module.courseModuleId,
        viewValue: `${module.name} (Sección: ${this.sectionMoveLabel(selectedSection)})`
      })) ?? []
    );
  }

  private _destroyed$ = new Subject<void>();

  private socketService = inject(SocketService);
  private ngZone = inject(NgZone);
  private modal = inject(ModalService);
  private auth = inject(AuthService);

  /** The TFM configuration is God's only (the API rejects everybody else): tutors and content directors do not see it. */
  get canConfigureTfm(): boolean {
    return this.courseRequiresTfm && this.auth.isGod();
  }

  /**
   * `sectionPanelTpl` is declared once (outside any `cdkDropList`) and reused via
   * `*ngTemplateOutlet` inside every drop list (the flat non-FP list and each year's own). CDK's
   * `CdkDrag` resolves its parent `CdkDropList` via `inject(CDK_DROP_LIST, {skipSelf: true})` in its
   * constructor — this never finds the ambient drop list for content rendered through a template
   * outlet (confirmed live: `dropContainer` stayed `undefined`, and the drop list's own registry —
   * `_unsortedItems`, populated purely by items self-registering, this CDK version has no
   * `ContentChildren` fallback — stayed empty). A `[ngTemplateOutletInjector]` providing
   * `CDK_DROP_LIST` doesn't fix it either: that injector is a plain environment `Injector`, and
   * `@SkipSelf()` NodeInjector lookups don't hop into one. So the drag always "starts" (DOM/pointer
   * events, no DI needed) but the drop list never receives it as a registered item, meaning a real
   * reorder never completes — the item's drag transform is simply never reset, so it stays wherever
   * it was dropped.
   *
   * Fix: register each section's `CdkDrag` with its ambient `CdkDropList` directly, using CDK's own
   * public `addItem`/`dropContainer` — the same effect the constructor's DI would have had if it
   * had resolved. `ngAfterViewChecked` re-checks every section-level drag on every check (cheap:
   * `sectionDrags`/`sectionDropLists` are typically single-digit counts, and the guard below skips
   * anything already registered) so newly added/reordered sections get wired up as they render.
   */
  @ViewChildren(CdkDrag) private sectionDrags?: QueryList<CdkDrag>;
  @ViewChildren(CdkDropList) private sectionDropLists?: QueryList<CdkDropList>;

  ngAfterViewChecked(): void {
    this.sectionDrags?.forEach((drag) => {
      if (drag.dropContainer || !drag.element.nativeElement.classList.contains('section-accordion')) return;
      const dropListEl = drag.element.nativeElement.closest('.sections-drop-list');
      const dropList = this.sectionDropLists?.find((dl) => dl.element.nativeElement === dropListEl);
      if (dropList) {
        drag.dropContainer = dropList;
        dropList.addItem(drag);
      }
    });
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private alert: AlertService,
    private api: ApiService,
    private toast: ToastService,
    private utils: UtilsService,
    private fb: FormBuilder,
    private chRef: ChangeDetectorRef,
    private iconRegistry: MatIconRegistry,
    private sanitizer: DomSanitizer,
    private settingsService: SettingsService
  ) {
    this.iconRegistry.addSvgIcon('other_icons', this.sanitizer.bypassSecurityTrustResourceUrl('assets/icon/other_icons.svg'));
  }

  static noWhitespaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null;
    }

    const isWhitespace = (control.value || '').toString().trim().length === 0;
    return isWhitespace ? { whitespace: true } : null;
  }

  static requiredNoWhitespace() {
    return [Validators.required, CourseContentComponent.noWhitespaceValidator];
  }

  async ngOnInit(): Promise<void> {
    await this.loadMessages();
    this.courseId = this.route.snapshot.paramMap.get('courseId');
    this.targetClassId = this.route.snapshot.queryParamMap.get('classId');

    // Load quiz feature flag from settings
    this.quizFeatureEnabled.set(!!this.settingsService.settings?.quizEnabled);

    if (this.courseId) {
      this.loadCourseContent();
    } else {
      this.isLoadingCourse = false;
      this.toast.presentErrorToast('No se pudo encontrar el ID del curso');
    }

    // Listen for sectionId changes to reset moduleId
    this.movingClassForm
      .get('sectionId')
      ?.valueChanges.pipe(takeUntil(this._destroyed$))
      .subscribe(() => {
        this.movingClassForm.get('moduleId')?.setValue(null);
      });

    // Listen for bulk notes generation WebSocket events using named handlers for proper cleanup
    this.socketService.socket.on(SocketEvent.BulkNotesGenerationProgress, this.bulkProgressHandler);
    this.socketService.socket.on(SocketEvent.BulkNotesGenerationComplete, this.bulkCompleteHandler);
    this.socketService.socket.on(SocketEvent.BulkQuizGenerationProgress, this.bulkQuizProgressHandler);
    this.socketService.socket.on(SocketEvent.BulkQuizGenerationComplete, this.bulkQuizCompleteHandler);
    this.socketService.socket.on('connect', this.socketReconnectHandler);
  }

  private bulkProgressHandler = (data: BulkNotesProgressEvent): void => {
    if (data.courseId !== this.courseId) return;
    this.ngZone.run(() => {
      this.isBulkGenerating = true;
      this.bulkBannerState = 'generating';
      this.bulkCompletedCount = data.completedCount;
      this.bulkTotalCount = data.totalCount;
      // Mark the class as pending review as soon as its notes are generated
      if (data.status === 'completed') {
        this.findClassFormGroupById(data.courseClassId)?.get('notesPendingReview')?.setValue(true, { emitEvent: false });
      }
    });
  };

  private bulkCompleteHandler = (data: BulkNotesCompleteEvent): void => {
    if (data.courseId !== this.courseId) return;
    this.ngZone.run(() => {
      this.isBulkGenerating = false;
      this.bulkBannerState = 'done';
      this.bulkCompletedCount = data.totalCount;
      this.bulkTotalCount = data.totalCount;
      this.bulkSuccessCount = data.successCount;
      this.bulkErrorCount = data.errorCount;
      // Ensure all successfully generated classes show the pending-review dot
      for (const result of data.results) {
        if (result.status === 'completed') {
          this.findClassFormGroupById(result.courseClassId)?.get('notesPendingReview')?.setValue(true, { emitEvent: false });
        }
      }
    });
  };

  private bulkQuizProgressHandler = (data: BulkQuizProgressEvent): void => {
    if (data.courseId !== this.courseId) return;
    this.ngZone.run(() => {
      this.isBulkQuizGenerating = true;
      this.bulkQuizBannerState = 'generating';
      this.bulkQuizCompletedCount = data.completedCount;
      this.bulkQuizTotalCount = data.totalCount;
      if (data.status === 'completed') {
        this.findClassFormGroupById(data.courseClassId)?.get('quizPendingReview')?.setValue(true, { emitEvent: false });
      }
    });
  };

  private bulkQuizCompleteHandler = (data: BulkQuizCompleteEvent): void => {
    if (data.courseId !== this.courseId) return;
    this.ngZone.run(() => {
      this.isBulkQuizGenerating = false;
      this.bulkQuizBannerState = 'done';
      this.bulkQuizCompletedCount = data.totalCount;
      this.bulkQuizTotalCount = data.totalCount;
      this.bulkQuizSuccessCount = data.successCount;
      this.bulkQuizErrorCount = data.errorCount;
      for (const result of data.results) {
        if (result.status === 'completed') {
          this.findClassFormGroupById(result.courseClassId)?.get('quizPendingReview')?.setValue(true, { emitEvent: false });
        }
      }
      this.toast.presentSuccessToast(`Autoevaluaciones generadas: ${data.successCount} completadas, ${data.errorCount} errores`);
    });
  };

  private socketReconnectHandler = (): void => {
    if (!this.isBulkGenerating) return;
    // If the socket reconnects while a generation is in progress, the complete
    // event was likely emitted while disconnected and is now permanently lost.
    // Reset the state so the button is re-enabled and the user can retry.
    this.ngZone.run(() => {
      this.isBulkGenerating = false;
      this.bulkBannerState = 'idle';
      this.toast.presentErrorToast('La conexión fue interrumpida. Verifica si los resúmenes se generaron correctamente.');
    });
  };

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
    // Use specific handler references to avoid removing listeners from child components
    this.socketService.socket.off(SocketEvent.BulkNotesGenerationProgress, this.bulkProgressHandler);
    this.socketService.socket.off(SocketEvent.BulkNotesGenerationComplete, this.bulkCompleteHandler);
    this.socketService.socket.off(SocketEvent.BulkQuizGenerationProgress, this.bulkQuizProgressHandler);
    this.socketService.socket.off(SocketEvent.BulkQuizGenerationComplete, this.bulkQuizCompleteHandler);
    this.socketService.socket.off('connect', this.socketReconnectHandler);
  }

  openBulkModal(): void {
    this.isBulkModalVisible = true;
  }

  closeBulkModal(): void {
    this.isBulkModalVisible = false;
  }

  openBulkQuizModal(): void {
    this.isBulkQuizModalVisible = true;
  }

  closeBulkQuizModal(): void {
    this.isBulkQuizModalVisible = false;
  }

  onBulkGenerationStarted(): void {
    this.isBulkGenerating = true;
    this.bulkBannerState = 'generating';
    this.bulkCompletedCount = 0;
    this.bulkTotalCount = 0;
    this.bulkSuccessCount = 0;
    this.bulkErrorCount = 0;
  }

  onBulkQuizGenerationStarted(): void {
    this.isBulkQuizGenerating = true;
    this.bulkQuizBannerState = 'generating';
    this.bulkQuizCompletedCount = 0;
    this.bulkQuizTotalCount = 0;
    this.bulkQuizSuccessCount = 0;
    this.bulkQuizErrorCount = 0;
  }

  dismissBulkBanner(): void {
    this.bulkBannerState = 'idle';
    this.bulkSuccessCount = 0;
    this.bulkErrorCount = 0;
    this.bulkCompletedCount = 0;
    this.bulkTotalCount = 0;
  }

  async onBulkQuizGenerate(): Promise<void> {
    // Kept for backwards compat — now opens the modal
    this.openBulkQuizModal();
  }

  dismissBulkQuizBanner(): void {
    this.bulkQuizBannerState = 'idle';
    this.bulkQuizSuccessCount = 0;
    this.bulkQuizErrorCount = 0;
    this.bulkQuizCompletedCount = 0;
    this.bulkQuizTotalCount = 0;
  }

  goToList(): void {
    this.router.navigate(['/god/courses'], {
      queryParamsHandling: 'preserve'
    });
  }

  async confirmCancelEditCourseContent(): Promise<any> {
    const header = 'Cancelar la edición de contenido de este curso';
    const message = `¿Estás seguro(a) de cancelar la edición de este curso?`;
    const message2 = `Se perderán los cambios realizados hasta el momento.`;
    const noDelete = 'Seguir editando';
    const remove = 'Salir';
    const goToList = await this.alert.presentConfirmRemoveActionAlert({
      title: header,
      message,
      message2,
      cancelText: noDelete,
      confirmText: remove
    });
    return goToList ? this.goToList() : null;
  }

  async loadCourseContent(): Promise<void> {
    try {
      if (!this.courseId) {
        this.isLoadingCourse = false;
        this.toast.presentErrorToast('No se ha proporcionado un ID de curso válido');
        return;
      }

      const basicDataResponse = await firstValueFrom(this.api.fetchCourseBasicData(this.courseId));
      this.courseName = basicDataResponse.course.name;
      this.courseRequiresTfm = !!basicDataResponse.course.requiresTfm;

      const response = await firstValueFrom(this.api.fetchCourseContentToEdit(this.courseId));
      this.courseToEdit = this.convertVimeoUrlsToIds(response.course);

      // FP courses only: group sections by academic year for rendering.
      this.courseToEdit.sections = sortSectionsByYear(this.courseToEdit.sections, this.courseToEdit.years);

      // Initialize calendar current file if exists
      if (this.courseToEdit.calendarImageUrl) {
        const fileName = this.courseToEdit.calendarImageUrl.split('/').pop() || 'calendario';
        this.calendarCurrentFile = {
          filename: fileName,
          path: this.courseToEdit.calendarImageUrl
        };
      }

      this.mapCurrentAttachments();
      this.initCourseForm(this.courseToEdit);
      this.initExpansionArrays();
      this.isLoadingCourse = false;
      // If course requires TFM, show the section directly (auto-open)
      if (this.courseRequiresTfm) {
        this.showTfmSection = true;
      } else {
        // Check TFM config existence in background (non-blocking) only if TFM is required
        // (kept for safety in case data is inconsistent, but section won't show)
        void this.checkTfmConfig();
      }
      if (this.targetClassId) {
        this.expandAndScrollToClass(this.targetClassId);
      }
    } catch {
      this.isLoadingCourse = false;
      this.toast.presentErrorToast('Hubo un error consultando curso');
      this.router.navigate(['/god/home']);
    }
  }

  private expandAndScrollToClass(classId: string): void {
    outer: for (let i = 0; i < this.courseToEdit.sections.length; i++) {
      for (let j = 0; j < this.courseToEdit.sections[i].modules.length; j++) {
        for (let k = 0; k < this.courseToEdit.sections[i].modules[j].classes.length; k++) {
          if (this.courseToEdit.sections[i].modules[j].classes[k].courseClassId === classId) {
            this.expandedSections[i] = true;
            this.expandedModules[i][j] = true;
            this.expandedClasses[i][j][k] = true;
            setTimeout(() => {
              const el = document.getElementById(`lesson-notes-${classId}`);
              el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
            break outer;
          }
        }
      }
    }
  }

  initExpansionArrays(): void {
    const hasSingleSection = this.courseToEdit.sections.length === 1;

    this.expandedSections = new Array(this.courseToEdit.sections.length).fill(false);

    if (hasSingleSection) {
      this.expandedSections[0] = true;
    }

    this.expandedModules = this.courseToEdit.sections.map((section) => new Array(section.modules.length).fill(false));

    this.expandedClasses = this.courseToEdit.sections.map((section) => section.modules.map((module) => new Array(module.classes.length).fill(false)));

    // FP courses only — same "auto-expand only when it's the sole panel" rule as expandedSections
    // above. All-true was tried first but every Año panel lives in the same <mat-accordion>, whose
    // default multi=false auto-closes previously-opened panels as later ones initialize — with 2+
    // years all requesting expanded=true at once, only the last one actually stayed open. [multi]
    // is also now set on that accordion so a manual multi-open works once the admin clicks around.
    const years = this.courseToEdit.years ?? [];
    this.expandedYears = new Array(years.length).fill(false);
    if (years.length === 1) {
      this.expandedYears[0] = true;
    }
  }

  initCourseForm(courseData: CourseToEdit): void {
    this.courseForm = this.fb.group({
      courseId: [courseData.courseId],
      years: this.fb.array(this.createYearsFormArray(courseData.years ?? [])),
      sections: this.fb.array(this.createSectionsFormArray(courseData.sections))
    });
  }

  createYearsFormArray(years: CourseYear[]): FormGroup[] {
    return years.map((year) => this.createYearFormGroup(year));
  }

  createYearFormGroup(year: CourseYear): FormGroup {
    return this.fb.group({
      courseYearId: [year.courseYearId],
      hidden: [year.hidden ?? false],
      publishDate: this.createPublishDateControl(year.publishDate as unknown as string | null)
    });
  }

  /**
   * The date picker enforces `[min]` as a validator on the *current value*, not only on new picks, so a
   * publishDate already saved in the past (an FP year that has already started, say) would leave the control —
   * and therefore the whole `courseForm` — invalid, silently disabling "Guardar". Remembering the loaded date
   * lets `minPublishDate` relax the minimum to it, while a newly picked date still has to be today or later.
   */
  private readonly savedPublishDateMin = new WeakMap<AbstractControl, Date>();

  private createPublishDateControl(savedPublishDate: string | null | undefined): FormControl {
    const savedDate = savedPublishDate ? DateTime.fromISO(savedPublishDate) : null;
    const control = new FormControl(savedDate);
    if (savedDate?.isValid && savedDate.toJSDate() < this.today) {
      this.savedPublishDateMin.set(control, savedDate.toJSDate());
    }
    return control;
  }

  minPublishDate(control: AbstractControl | null): Date {
    return (control && this.savedPublishDateMin.get(control)) || this.today;
  }

  createSectionsFormArray(sections: SectionToEdit[]): FormGroup[] {
    return sections.map((section) => this.createSectionFormGroup(section));
  }

  createSectionFormGroup(section: SectionToEdit): FormGroup {
    return this.fb.group({
      courseSectionId: [section.courseSectionId],
      courseYearId: [section.courseYearId ?? null],
      name: [section.name, CourseContentComponent.requiredNoWhitespace()],
      attachments: [section.attachments],
      isLocal: [section.isLocal],
      acceptClassFilesLimit: [section.acceptClassFilesLimit],
      filesToUpload: [section.filesToUpload],
      hidden: [section.hidden],
      isDeletingRemoteAttachment: [section.isDeletingRemoteAttachment],
      showPromotion: [section.showPromotion],
      modules: this.fb.array(this.createModulesFormArray(section.modules))
    });
  }

  createModulesFormArray(modules: ModuleToEdit[]): FormGroup[] {
    return modules.map((module) => this.createModuleFormGroup(module));
  }

  createModuleFormGroup(module: ModuleToEdit): FormGroup {
    const showPromotion = module.showPromotion || false;

    return this.fb.group({
      courseModuleId: [module.courseModuleId],
      name: [module.name, CourseContentComponent.requiredNoWhitespace()],
      attachments: [module.attachments],
      isLocal: [module.isLocal],
      acceptClassFilesLimit: [module.acceptClassFilesLimit],
      filesToUpload: [module.filesToUpload],
      hidden: [module.hidden],
      publishDate: this.createPublishDateControl(module.publishDate as unknown as string | null),
      showPromotion: [showPromotion],
      promotionTitle: [module.promotionTitle || '', showPromotion ? CourseContentComponent.requiredNoWhitespace() : null],
      promotionDescription: [module.promotionDescription || '', showPromotion ? CourseContentComponent.requiredNoWhitespace() : null],
      promotionButtonText: [module.promotionButtonText || '', showPromotion ? CourseContentComponent.requiredNoWhitespace() : null],
      promotionButtonUrl: [
        module.promotionButtonUrl || '',
        showPromotion ? [Validators.required, CourseContentComponent.noWhitespaceValidator, Validators.pattern(/^https:\/\/[^\s]+$/)] : null
      ],
      classes: this.fb.array(this.createClassesFormArray(module.classes))
    });
  }

  createClassesFormArray(classes: ClassToEdit[]): FormGroup[] {
    return classes.map((classItem) => this.createClassFormGroup(classItem));
  }

  createClassFormGroup(classItem: ClassToEdit): FormGroup {
    const messages =
      classItem.messages && classItem.messages.length
        ? classItem.messages
        : [
            {
              enabled: false,
              messageId: null
            }
          ];
    return this.fb.group({
      courseClassId: [classItem.courseClassId],
      name: [classItem.name, CourseContentComponent.requiredNoWhitespace()],
      attachments: [classItem.attachments],
      isLocal: [classItem.isLocal],
      acceptClassFilesLimit: [classItem.acceptClassFilesLimit],
      filesToUpload: [classItem.filesToUpload],
      hidden: [classItem.hidden],
      isDeletingRemoteAttachment: [classItem.isDeletingRemoteAttachment],
      // Lazily loaded when the class accordion is first expanded (classVideoUrl, description, attachments, hidden).
      // For local (new) classes all content is available immediately; for remote classes _contentLoaded starts
      // false and is set to true after getCourseClassDetail resolves.
      _contentLoaded: [classItem.isLocal === true],
      description: [classItem.description ?? null],
      // Validators for classVideoUrl are deferred: added after content loads to avoid false
      // invalidity in the accordion header for classes the admin has never expanded.
      classVideoUrl: [
        classItem.classVideoUrl ?? '',
        classItem.isLocal === true ? [Validators.required, CourseContentComponent.noWhitespaceValidator, Validators.pattern(/^\d{9,10}$/)] : []
      ],
      notesPendingReview: [!!(classItem as any).notesPendingReview], // computed by API from notesStatus & notesReviewedAt
      quizPendingReview: [!!(classItem as any).quizPendingReview],
      messages: this.fb.array(this.createClassMessagesFormArray(messages))
    });
  }

  createClassMessagesFormArray(messages: { enabled: boolean; messageId: string | null }[]): FormGroup[] {
    return messages.map((message) => this.createClassMessageFormGroup(message));
  }

  createClassMessageFormGroup(message: { enabled: boolean; messageId: string | null }): FormGroup {
    const group = this.fb.group({
      enabled: [message.enabled],
      messageId: [message.messageId]
    });

    // Listener para actualizar dinámicamente los validadores
    const enabledControl = group.get('enabled');
    const messageIdControl = group.get('messageId');

    if (enabledControl && messageIdControl) {
      enabledControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((enabled: boolean | null) => {
        if (enabled === true) {
          messageIdControl.setValidators([Validators.required]);
          messageIdControl.enable();
        } else {
          messageIdControl.clearValidators();
          messageIdControl.setValue(null);
          messageIdControl.disable();
        }
        messageIdControl.updateValueAndValidity();
        this.courseForm.markAllAsTouched();
        this.courseForm.markAsDirty();
      });

      // Twilio template fetching — replaced by Kommo bot info display
      // messageIdControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe((newMessageId: string | null) => {
      //   if (newMessageId) {
      //     const message = this.messages.find((msg) => msg.messageId === newMessageId);
      //
      //     if (message && message.twilioContentSID && !this.loadedTemplates.has(message.twilioContentSID)) {
      //       this.api.getTwilioTemplateBySid(message.twilioContentSID).subscribe(
      //         (response) => {
      //           if (response.data && response.data.body) {
      //             this.loadedTemplates.set(message.twilioContentSID!, response.data.body);
      //             this.chRef.detectChanges();
      //           }
      //         },
      //         (error) => {
      //           console.error('Error loading message template:', error);
      //         }
      //       );
      //     }
      //   }
      // });
      messageIdControl.valueChanges.pipe(takeUntil(this._destroyed$)).subscribe(() => {
        this.chRef.detectChanges();
      });

      // Validar inicialmente
      if (message.enabled) {
        messageIdControl.setValidators([Validators.required]);
        messageIdControl.updateValueAndValidity({ emitEvent: false });
      } else {
        messageIdControl.clearValidators();
        messageIdControl.setValue(null);
        messageIdControl.disable();
      }
    }

    return group;
  }

  async mapCurrentAttachments(): Promise<void> {
    this.isLoadingAttachments = true;
    this.currentAttachments = {
      sections: []
    };

    this.fetchedUrls = {
      sections: [],
      classes: []
    };

    if (!this.courseToEdit.sections.length) {
      const newSection = new SectionToEdit();
      this.courseToEdit.sections = [newSection];
    } else {
      for (let i = 0; i < this.courseToEdit.sections.length; i++) {
        const section = this.courseToEdit.sections[i];
        section.isDeletingRemoteAttachment = false;
        section.acceptClassFilesLimit = 10;

        if (!section.filesToUpload) {
          section.filesToUpload = [];
        }

        this.currentAttachments.sections[i] = {
          attachments: [],
          modules: []
        };

        this.fetchedUrls.sections[i] = false;

        if (!section.modules.length) {
          section.modules = [new ModuleToEdit()];
        }

        this.fetchedUrls.classes[i] = [];

        for (let j = 0; j < section.modules.length; j++) {
          const module = section.modules[j];

          this.currentAttachments.sections[i].modules[j] = {
            classes: []
          };

          if (!module.classes.length) {
            module.classes = [new ClassToEdit()];
          }

          this.fetchedUrls.classes[i][j] = [];

          for (let k = 0; k < module.classes.length; k++) {
            const courseClass = module.classes[k];
            courseClass.acceptClassFilesLimit = 10;
            courseClass.isDeletingRemoteAttachment = false;

            if (!courseClass.filesToUpload) {
              courseClass.filesToUpload = [];
            }

            this.currentAttachments.sections[i].modules[j].classes[k] = {
              attachments: []
            };

            this.fetchedUrls.classes[i][j][k] = false;
          }
        }
      }
    }

    this.isLoadingAttachments = false;
  }

  async fetchSectionAttachmentUrls(sectionIndex: number): Promise<void> {
    if (this.fetchedUrls.sections[sectionIndex]) {
      return;
    }
    const section = this.courseToEdit.sections[sectionIndex];

    if (section.attachments && section.attachments.length) {
      const sectionAttachments = [];
      for (const attachment of section.attachments) {
        try {
          const response = await firstValueFrom(this.api.getSectionFilePresignedUrl(this.courseId!, section.courseSectionId, attachment.name));

          sectionAttachments.push({
            filename: attachment.name,
            path: attachment.url,
            presignedUrl: response.presignedUrl || response.url
          });
        } catch (error) {
          console.error('Error obteniendo URL prefirmada para archivo de sección:', error);
          sectionAttachments.push({
            filename: attachment.name,
            path: attachment.url,
            presignedUrl: null
          });
        }
      }
      this.currentAttachments.sections[sectionIndex].attachments = sectionAttachments;
    }

    this.fetchedUrls.sections[sectionIndex] = true;
  }

  async fetchClassAttachmentUrls(sectionIndex: number, moduleIndex: number, classIndex: number): Promise<void> {
    if (this.fetchedUrls.classes[sectionIndex]?.[moduleIndex]?.[classIndex]) {
      return;
    }

    const section = this.courseToEdit.sections[sectionIndex];
    const module = section.modules[moduleIndex];
    const courseClass = module.classes[classIndex];

    if (courseClass.attachments && courseClass.attachments.length) {
      const classAttachments = [];
      for (const attachment of courseClass.attachments) {
        try {
          const response = await firstValueFrom(
            this.api.getClassFilePresignedUrl(
              this.courseId!,
              section.courseSectionId,
              module.courseModuleId,
              courseClass.courseClassId,
              attachment.name
            )
          );
          classAttachments.push({
            filename: attachment.name,
            path: attachment.url,
            presignedUrl: response.presignedUrl || response.url
          });
        } catch (error) {
          console.error('Error obteniendo URL prefirmada para archivo de clase:', error);
          classAttachments.push({
            filename: attachment.name,
            path: attachment.url,
            presignedUrl: null
          });
        }
      }
      this.currentAttachments.sections[sectionIndex].modules[moduleIndex].classes[classIndex].attachments = classAttachments;
    }

    if (!this.fetchedUrls.classes[sectionIndex]) {
      this.fetchedUrls.classes[sectionIndex] = [];
    }
    if (!this.fetchedUrls.classes[sectionIndex][moduleIndex]) {
      this.fetchedUrls.classes[sectionIndex][moduleIndex] = [];
    }
    this.fetchedUrls.classes[sectionIndex][moduleIndex][classIndex] = true;
  }

  async onSectionExpand(sectionIndex: number): Promise<void> {
    this.expandedSections[sectionIndex] = true;
    await this.fetchSectionAttachmentUrls(sectionIndex);
  }

  async onModuleExpand(sectionIndex: number, moduleIndex: number): Promise<void> {
    this.expandedModules[sectionIndex] = this.expandedModules[sectionIndex].map((_, index) => index === moduleIndex);

    this.expandedClasses[sectionIndex] = this.expandedClasses[sectionIndex].map((moduleClasses, index) =>
      index === moduleIndex ? moduleClasses : moduleClasses.map(() => false)
    );
  }

  async onClassExpand(sectionIndex: number, moduleIndex: number, classIndex: number): Promise<void> {
    this.sectionIndex = sectionIndex;
    this.moduleIndex = moduleIndex;
    this.classIndex = classIndex;
    if (!this.expandedClasses[sectionIndex]) {
      this.expandedClasses[sectionIndex] = [];
    }
    if (!this.expandedClasses[sectionIndex][moduleIndex]) {
      this.expandedClasses[sectionIndex][moduleIndex] = [];
    }

    this.expandedClasses[sectionIndex][moduleIndex] = this.expandedClasses[sectionIndex][moduleIndex].map((_, index) => index === classIndex);

    this.updateClassMessage(sectionIndex, moduleIndex, classIndex);

    // Content must be loaded before attachment URLs are fetched,
    // because fetchClassAttachmentUrls reads from courseToEdit which is populated here.
    await this.loadClassContentIfNeeded(sectionIndex, moduleIndex, classIndex);
    await this.fetchClassAttachmentUrls(sectionIndex, moduleIndex, classIndex);
  }

  private async loadClassContentIfNeeded(sectionIndex: number, moduleIndex: number, classIndex: number): Promise<void> {
    const classGroup = this.getClassFormGroup(sectionIndex, moduleIndex, classIndex);
    if (classGroup.get('_contentLoaded')?.value) return;

    const classId = classGroup.get('courseClassId')?.value;
    if (!classId) return;

    try {
      const response = await firstValueFrom(this.api.fetchCourseClassDetail(classId));
      const classVideoId = extractVimeoVideoId(response.classVideoUrl) ?? response.classVideoUrl ?? '';
      classGroup.patchValue(
        {
          classVideoUrl: classVideoId,
          description: response.description,
          hidden: response.hidden,
          attachments: response.attachments
        },
        { emitEvent: false }
      );

      // Enable validators now that classVideoUrl is known
      classGroup
        .get('classVideoUrl')
        ?.setValidators([Validators.required, CourseContentComponent.noWhitespaceValidator, Validators.pattern(/^\d{9,10}$/)]);
      classGroup.get('classVideoUrl')?.updateValueAndValidity({ emitEvent: false });

      // Populate courseToEdit so fetchClassAttachmentUrls can build presigned URLs,
      // and so onClassHiddenChange can compare against the real initial value.
      if (this.courseToEdit.sections[sectionIndex]?.modules[moduleIndex]?.classes[classIndex]) {
        this.courseToEdit.sections[sectionIndex].modules[moduleIndex].classes[classIndex].attachments = response.attachments;
        this.courseToEdit.sections[sectionIndex].modules[moduleIndex].classes[classIndex].hidden = response.hidden;
      }
    } catch {
      // Leave fields at defaults; backend preserves DB values for unexpanded classes
    } finally {
      classGroup.get('_contentLoaded')?.setValue(true, { emitEvent: false });
    }
  }

  private async updateClassMessage(sectionIndex: number, moduleIndex: number, classIndex: number, _refresh = false): Promise<void> {
    // Twilio template fetching — replaced by Kommo bot info display
    // const selectedClasss = this.courseToEdit.sections[sectionIndex].modules[moduleIndex].classes[classIndex];
    // const selectedClassMessage = selectedClasss.messages[0];
    //
    // if (selectedClassMessage && selectedClassMessage.enabled && selectedClassMessage.messageId) {
    //   const messageId = selectedClassMessage.messageId;
    //   const message = this.messages.find((msg) => msg.messageId === messageId);
    //
    //   if (message && message.twilioContentSID && !this.loadedTemplates.has(message.twilioContentSID)) {
    //     try {
    //       const response = await firstValueFrom(this.api.getTwilioTemplateBySid(message?.twilioContentSID));
    //       if (response.data && response.data.body) {
    //         this.loadedTemplates.set(message.twilioContentSID, response.data.body);
    //         this.chRef.detectChanges();
    //       }
    //     } catch (error) {
    //       console.error('Error loading message details:', error);
    //     }
    //   }
    // }
    this.chRef.detectChanges();
  }

  async addSection(i: number): Promise<void> {
    // Preserves the year of the section it's inserted after, so within a FP
    // year all "add next" insertions stay tagged to that year. null for
    // standard/flash courses (courseYearId always null there). Clamp to the
    // last section when i is past the end (the "append at the very end"
    // call from the global Añadir asignatura button, hidden for FP courses).
    const anchorIndex = Math.min(i, this.courseToEdit.sections.length - 1);
    const courseYearId = this.courseToEdit.sections[anchorIndex]?.courseYearId ?? null;
    await this.insertSectionAt(i + 1, courseYearId);
  }

  /**
   * FP courses only: adds a new section (asignatura) to the given academic
   * year, at the end of that year's block of sections — or, if the year has
   * no sections yet, right before the first section of the next year (or at
   * the end of the course if it's the last/only year).
   */
  async addSectionToYear(courseYearId: string): Promise<void> {
    const sections = this.courseToEdit.sections;
    let lastIndexOfYear = -1;
    for (let idx = 0; idx < sections.length; idx++) {
      if (sections[idx].courseYearId === courseYearId) lastIndexOfYear = idx;
    }

    if (lastIndexOfYear !== -1) {
      await this.insertSectionAt(lastIndexOfYear + 1, courseYearId);
      return;
    }

    // Year has no sections yet: insert right before the first section
    // belonging to a later year, using the years' own order.
    const years = this.courseToEdit.years ?? [];
    const thisYearOrder = years.findIndex((year) => year.courseYearId === courseYearId);
    const laterYearIds = new Set(years.slice(thisYearOrder + 1).map((year) => year.courseYearId));
    const insertBeforeIndex = sections.findIndex((section) => section.courseYearId && laterYearIds.has(section.courseYearId));

    await this.insertSectionAt(insertBeforeIndex === -1 ? sections.length : insertBeforeIndex, courseYearId);
  }

  /**
   * Shared insertion logic for addSection/addSectionToYear: inserts a new,
   * empty section (with one empty module/class) at `insertIndex`, keeping
   * courseToEdit.sections, the sectionsFormArray, and every index-parallel
   * bookkeeping array (currentAttachments, expandedSections/Modules/Classes)
   * in sync.
   */
  private async insertSectionAt(insertIndex: number, courseYearId: string | null): Promise<void> {
    try {
      const newSection = new SectionToEdit();
      newSection.isDeletingRemoteAttachment = false;
      newSection.acceptClassFilesLimit = 10;
      newSection.courseYearId = courseYearId;

      const newModule = new ModuleToEdit();

      const newClass = new ClassToEdit();
      newClass.acceptClassFilesLimit = 10;
      newClass.isDeletingRemoteAttachment = false;

      newModule.classes = [newClass];
      newSection.modules = [newModule];

      this.courseToEdit.sections.splice(insertIndex, 0, newSection);

      this.sectionsFormArray.insert(insertIndex, this.createSectionFormGroup(newSection));

      const newSectionAttachments = {
        attachments: [],
        modules: [
          {
            classes: [
              {
                attachments: []
              }
            ]
          }
        ]
      };

      if (!this.currentAttachments.sections) {
        this.currentAttachments.sections = [];
      }

      this.currentAttachments.sections.splice(insertIndex, 0, newSectionAttachments);

      this.expandedSections.splice(insertIndex, 0, true);
      this.expandedModules.splice(insertIndex, 0, [false]);
      this.expandedClasses.splice(insertIndex, 0, [[false]]);

      this.toast.presentSuccessToast('Nueva asignatura añadida');
    } catch (error) {
      console.error('Error al añadir asignatura:', error);
      this.toast.presentErrorToast('Error al añadir asignatura');
    }
  }

  /** FP courses only — true once the course has academic years; drives the Año-wraps-Asignatura accordion nesting. */
  get isFpCourse(): boolean {
    return !!this.courseToEdit?.years?.length;
  }

  getYearNumber(courseYearId: string | null): number | null {
    return this.courseToEdit.years?.find((year) => year.courseYearId === courseYearId)?.number ?? null;
  }

  /**
   * FP courses only — flat `courseToEdit.sections`/`sectionsFormArray` indices belonging to one
   * academic year, in course order. Renders that year's asignaturas (via the shared
   * `sectionPanelTpl`) nested inside its own "Año" accordion panel, without needing to touch the
   * flat, index-based bookkeeping (expandedSections/Modules/Classes, sectionsFormArray) used by
   * every asignatura/módulo/clase — same low-risk approach the original FP content-editing work
   * chose (see ENF-2272 plan, Fase 5). Empty years (no sections yet) simply get an empty list here,
   * which also fixes the previous limitation where an empty year rendered out of order at the end.
   */
  getYearSectionIndices(courseYearId: string): number[] {
    const indices: number[] = [];
    this.courseToEdit.sections.forEach((section, i) => {
      if (section.courseYearId === courseYearId) indices.push(i);
    });
    return indices;
  }

  /**
   * Whether section `i` can be deleted: a FP year must always keep at least one asignatura (with
   * one módulo/clase — enforced separately by deleteLocalModule/deleteLocalClass, which are
   * already scoped per-section), so this checks the section count *within that section's own
   * year*, not `courseToEdit.sections.length` — that flat array spans every year of the course,
   * so a whole-course count would wrongly allow emptying out a single year that still has other
   * years' sections propping up the total. Non-FP courses (no courseYearId) fall back to the
   * previous whole-course check, since there's only one implicit "year" there.
   */
  canDeleteSection(i: number): boolean {
    const courseYearId = this.courseToEdit.sections[i]?.courseYearId;
    if (!courseYearId) return this.courseToEdit.sections.length > 1;
    return this.getYearSectionIndices(courseYearId).length > 1;
  }

  /**
   * The "Asignatura N" number shown/used everywhere a section is referenced by position (header
   * label, delete confirmations/toasts, the `sectionNumber` sent to the backend for its own
   * success message). For a non-FP course (or a section without a year) this is just the flat
   * `i + 1`, same as always. For a FP course it's the 1-based position of section `i` *within its
   * own year* — Año is a higher-level grouping, so asignatura numbering restarts at 1 per year
   * instead of continuing across the whole course.
   */
  getSectionDisplayNumber(i: number): number {
    const courseYearId = this.courseToEdit.sections[i]?.courseYearId;
    if (!courseYearId) return i + 1;
    return this.getYearSectionIndices(courseYearId).indexOf(i) + 1;
  }

  /**
   * Adds one new academic year at the end of the course — purely local until the next "Guardar",
   * same pattern as "Añadir asignatura" (insertSectionAt): `new SectionToEdit()` already generates
   * a real client-side id and cascades to one local module/class, so saveCourseContent can create
   * it with a normal upsert on save, no different from any other new section/module/class. New
   * year is always the highest number, so its section is simply appended at the end of the flat
   * `sections` array/FormArray — no reordering needed, unlike addSectionToYear which must find
   * where an existing year's block ends.
   */
  addYear(): void {
    if (!this.courseToEdit.years) this.courseToEdit.years = [];
    const years = this.courseToEdit.years;

    const newYear: CourseYear = {
      courseYearId: uuidv4(),
      number: years.length + 1,
      hidden: true,
      publishDate: null,
      isLocal: true
    };
    years.push(newYear);
    this.yearsFormArray.push(this.createYearFormGroup(newYear));
    this.expandedYears.push(true);

    const newSection = new SectionToEdit();
    newSection.courseYearId = newYear.courseYearId;

    const insertIndex = this.courseToEdit.sections.length;
    this.courseToEdit.sections.push(newSection);
    this.sectionsFormArray.insert(insertIndex, this.createSectionFormGroup(newSection));

    if (!this.currentAttachments.sections) this.currentAttachments.sections = [];
    this.currentAttachments.sections.push({ attachments: [], modules: [{ classes: [{ attachments: [] }] }] });

    this.expandedSections.push(false);
    this.expandedModules.push([false]);
    this.expandedClasses.push([[false]]);

    this.toast.presentSuccessToast(`Año ${newYear.number} añadido`);
  }

  /** Same confirm pattern as confirmDeleteLocalSection/confirmDeleteRemoteSection — the template only shows the trash icon when there's more than one year, but always asks before deleting one (a year always has content once created, per ensureYears). */
  async confirmDeleteYear(yearIndex: number): Promise<any> {
    const year = this.courseToEdit.years?.[yearIndex];
    if (!year) return;

    const header = 'Eliminar año';
    const message = `¿Estás seguro(a) de Eliminar el Año ${year.number}? Esta acción no se podrá deshacer y todo su contenido (asignaturas, módulos y clases) dejará de estar disponible.`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const shouldDelete = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return shouldDelete ? this.deleteYear(yearIndex) : null;
  }

  async deleteYear(yearIndex: number): Promise<void> {
    const years = this.courseToEdit.years;
    const year = years?.[yearIndex];
    if (!years || !year || years.length <= 1) return;

    // A year added in this session and never saved only ever existed in local state — nothing to
    // call, unlike a persisted year, which still needs the immediate cascade delete (same UX as
    // "Eliminar asignatura" on a remote section).
    if (!year.isLocal) {
      try {
        const response = await firstValueFrom(this.api.deleteCourseYear({ courseId: this.courseToEdit.courseId, courseYearId: year.courseYearId }));
        this.toast.presentSuccessToast(response.message);
      } catch (errorResponse: any) {
        const errorMessage = errorResponse?.error?.message;
        this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando el año');
        return;
      }
    }

    // Remove this year's sections from every flat, index-parallel bookkeeping array — descending
    // order so removing one doesn't shift the indices of the ones still to be removed.
    const indicesToRemove = this.getYearSectionIndices(year.courseYearId).sort((a, b) => b - a);
    for (const i of indicesToRemove) {
      this.courseToEdit.sections.splice(i, 1);
      this.sectionsFormArray.removeAt(i);
      this.expandedSections.splice(i, 1);
      this.expandedModules.splice(i, 1);
      this.expandedClasses.splice(i, 1);
      if (this.currentAttachments.sections[i]) this.currentAttachments.sections.splice(i, 1);
      if (this.fetchedUrls.sections[i] !== undefined) this.fetchedUrls.sections.splice(i, 1);
      if (this.fetchedUrls.classes[i]) this.fetchedUrls.classes.splice(i, 1);
    }

    // Remove the year itself and renumber every remaining year above it down by one — years are
    // strictly sequential and non-renameable, mirroring the backend's own renumbering.
    years.splice(yearIndex, 1);
    this.yearsFormArray.removeAt(yearIndex);
    this.expandedYears.splice(yearIndex, 1);
    for (const remainingYear of years) {
      if (remainingYear.number > year.number) remainingYear.number -= 1;
    }

    if (year.isLocal) {
      this.toast.presentSuccessToast(`Año ${year.number} borrado`);
    }
  }

  /** Shows the TFM section when user clicks "Añadir TFM". */
  addTfmSection(): void {
    this.showTfmSection = true;
  }

  /** Called by admin-tfm-config when its config is loaded: show if exists. */
  onTfmConfigExists(exists: boolean): void {
    if (exists) {
      this.showTfmSection = true;
    }
  }

  /** Silently checks if the course already has a TFM config on page load. */
  private async checkTfmConfig(): Promise<void> {
    if (!this.courseId) return;
    try {
      const response = await firstValueFrom(this.api.getTfmConfig(this.courseId));
      if (response?.data) {
        this.showTfmSection = true;
      }
    } catch {
      // TFM config not found — keep showTfmSection = false
    }
  }

  async confirmDeleteLocalSection(i: number): Promise<any> {
    const header = 'Eliminar asignatura';
    const message = `¿Estás seguro(a) de Eliminar la Asignatura ${this.getSectionDisplayNumber(i)}? Esta acción no se podrá deshacer y dejará de estar disponible.`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteLocalSection(i) : null;
  }

  async deleteLocalSection(i: number): Promise<void> {
    if (!this.canDeleteSection(i)) return;

    // Captured before splicing — afterwards index i holds a different section (or none).
    const sectionNumber = this.getSectionDisplayNumber(i);

    this.courseToEdit.sections.splice(i, 1);

    this.sectionsFormArray.removeAt(i);

    if (this.currentAttachments.sections[i]) {
      this.currentAttachments.sections.splice(i, 1);
    }

    this.toast.presentSuccessToast(`Asignatura ${sectionNumber} borrada`);
  }

  async confirmDeleteRemoteSection(i: number, section: SectionToEdit): Promise<any> {
    const header = 'Eliminar asignatura';
    const message = `¿Estás seguro(a) de Eliminar la Asignatura ${this.getSectionDisplayNumber(i)}? Esta acción no se podrá deshacer y dejará de estar disponible.`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteRemoteSection(i, section) : null;
  }

  async deleteRemoteSection(i: number, section: SectionToEdit): Promise<void> {
    try {
      if (!this.canDeleteSection(i)) return;

      const courseSectionId = section.courseSectionId;
      const sectionNumber = this.getSectionDisplayNumber(i);
      const body: any = {
        courseId: this.courseToEdit.courseId,
        courseSectionId,
        sectionNumber
      };

      const response = await firstValueFrom(this.api.deleteCourseSection(body));

      this.courseToEdit.sections.splice(i, 1);
      this.sectionsFormArray.removeAt(i);

      if (this.currentAttachments.sections[i]) {
        this.currentAttachments.sections.splice(i, 1);
      }

      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando asignatura');
    }
  }

  async addModule(i: number, j: number): Promise<void> {
    try {
      const newModule = new ModuleToEdit();

      const newClass = new ClassToEdit();
      newClass.acceptClassFilesLimit = 10;
      newClass.isDeletingRemoteAttachment = false;

      newModule.classes = [newClass];

      this.courseToEdit.sections[i].modules.splice(j + 1, 0, newModule);

      this.getModulesFormArray(i).insert(j + 1, this.createModuleFormGroup(newModule));

      const newModuleAttachments = {
        classes: [
          {
            attachments: []
          }
        ]
      };

      if (!this.currentAttachments.sections[i]) {
        this.currentAttachments.sections[i] = {
          attachments: [],
          modules: []
        };
      }

      this.currentAttachments.sections[i].modules.splice(j + 1, 0, newModuleAttachments);

      this.expandedSections[i] = true;

      if (!this.expandedModules[i]) {
        this.expandedModules[i] = [];
      }

      const updatedModulesExpanded: boolean[] = [];

      for (let moduleIndex = 0; moduleIndex < this.courseToEdit.sections[i].modules.length; moduleIndex++) {
        updatedModulesExpanded[moduleIndex] = moduleIndex === j;
      }
      this.expandedModules[i] = updatedModulesExpanded;

      if (!this.expandedClasses[i]) {
        this.expandedClasses[i] = [];
      }

      while (this.expandedClasses[i].length < this.courseToEdit.sections[i].modules.length) {
        this.expandedClasses[i].push([]);
      }

      this.expandedClasses[i][j] = [false];

      this.toast.presentSuccessToast('Nuevo módulo añadido');
    } catch (error) {
      console.error('Error al añadir módulo:', error);
      this.toast.presentErrorToast('Error al añadir módulo');
    }
  }

  async confirmDeleteLocalModule(i: number, j: number): Promise<any> {
    const header = 'Eliminar módulo';
    const message = `¿Estás seguro(a) de Eliminar el Módulo ${j + 1} de la Asignatura ${this.getSectionDisplayNumber(
      i
    )}? Esta acción no se podrá deshacer y dejará de estar disponible`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteLocalModule(i, j) : null;
  }

  async deleteLocalModule(i: number, j: number): Promise<void> {
    if (this.courseToEdit.sections[i].modules.length - 1 === 0) return;

    this.courseToEdit.sections[i].modules.splice(j, 1);

    this.getModulesFormArray(i).removeAt(j);

    if (this.currentAttachments.sections[i]?.modules[j]) {
      this.currentAttachments.sections[i].modules.splice(j, 1);
    }

    this.toast.presentSuccessToast(`Módulo ${j + 1} borrado de Asignatura ${this.getSectionDisplayNumber(i)}`);
  }

  async confirmDeleteRemoteModule(i: number, j: number): Promise<any> {
    const header = 'Eliminar módulo';
    const message = `¿Estás seguro(a) de Eliminar el Módulo ${j + 1} de la Asignatura ${this.getSectionDisplayNumber(
      i
    )}? Esta acción no se podrá deshacer y dejará de estar disponible`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteRemoteModule(i, j) : null;
  }

  async deleteRemoteModule(i: number, j: number): Promise<void> {
    try {
      if (this.courseToEdit.sections[i].modules.length - 1 === 0) return;

      const sectionNumber = this.getSectionDisplayNumber(i);
      const moduleNumber = j + 1;
      const body: any = {
        courseId: this.courseToEdit.courseId,
        courseSectionId: this.courseToEdit.sections[i].courseSectionId,
        courseModuleId: this.courseToEdit.sections[i].modules[j].courseModuleId,
        sectionNumber,
        moduleNumber
      };

      const response = await firstValueFrom(this.api.deleteCourseModule(body));

      this.courseToEdit.sections[i].modules.splice(j, 1);
      this.getModulesFormArray(i).removeAt(j);

      if (this.currentAttachments.sections[i]?.modules[j]) {
        this.currentAttachments.sections[i].modules.splice(j, 1);
      }

      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando módulo');
    }
  }

  async addClass(i: number, j: number, k: number): Promise<void> {
    try {
      const newClass = new ClassToEdit();
      newClass.acceptClassFilesLimit = 10;
      newClass.isDeletingRemoteAttachment = false;

      this.courseToEdit.sections[i].modules[j].classes.splice(k + 1, 0, newClass);

      this.getClassesFormArray(i, j).insert(k + 1, this.createClassFormGroup(newClass));

      if (!this.currentAttachments.sections[i]) {
        this.currentAttachments.sections[i] = {
          attachments: [],
          modules: []
        };
      }

      if (!this.currentAttachments.sections[i].modules[j]) {
        this.currentAttachments.sections[i].modules[j] = {
          classes: []
        };
      }

      this.currentAttachments.sections[i].modules[j].classes.splice(k + 1, 0, {
        attachments: []
      });

      this.expandedSections[i] = true;

      if (!this.expandedModules[i]) {
        this.expandedModules[i] = [];
      }
      this.expandedModules[i][j] = true;

      if (!this.expandedClasses[i]) {
        this.expandedClasses[i] = [];
      }
      if (!this.expandedClasses[i][j]) {
        this.expandedClasses[i][j] = [];
      }

      const updatedClassesExpanded: boolean[] = [];

      for (let classIndex = 0; classIndex < this.courseToEdit.sections[i].modules[j].classes.length; classIndex++) {
        updatedClassesExpanded[classIndex] = classIndex === k;
      }

      this.expandedClasses[i][j] = updatedClassesExpanded;

      this.toast.presentSuccessToast('Nueva clase añadida');
    } catch (error) {
      console.error('Error al añadir clase:', error);
      this.toast.presentErrorToast('Error al añadir clase');
    }
  }

  async confirmDeleteLocalClass(i: number, j: number, k: number): Promise<any> {
    const header = 'Eliminar clase';
    const message = `¿Estás seguro(a) de Eliminar la Clase ${k + 1} del Módulo ${j + 1} de la Asignatura ${this.getSectionDisplayNumber(
      i
    )}? Esta acción no se podrá deshacer y dejará de estar disponible`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteLocalClass(i, j, k) : null;
  }

  async deleteLocalClass(i: number, j: number, k: number): Promise<void> {
    if (this.courseToEdit.sections[i].modules[j].classes.length - 1 === 0) return;

    this.courseToEdit.sections[i].modules[j].classes.splice(k, 1);

    this.getClassesFormArray(i, j).removeAt(k);

    if (this.currentAttachments.sections[i]?.modules[j]?.classes[k]) {
      this.currentAttachments.sections[i].modules[j].classes.splice(k, 1);
    }

    this.toast.presentSuccessToast(`Clase ${k + 1} borrada del Módulo ${j + 1} de la Asignatura ${this.getSectionDisplayNumber(i)}`);
  }

  async confirmDeleteRemoteClass(i: number, j: number, k: number): Promise<any> {
    const header = 'Eliminar clase';
    const message = `¿Estás seguro(a) de Eliminar la Clase ${k + 1} del Módulo ${j + 1} de la Asignatura ${this.getSectionDisplayNumber(
      i
    )}? Esta acción no se podrá deshacer y dejará de estar disponible`;
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const deleteSection = await this.alert.presentConfirmActionAlert(header, message, noDelete, remove);
    return deleteSection ? this.deleteRemoteClass(i, j, k) : null;
  }

  async deleteRemoteClass(i: number, j: number, k: number): Promise<void> {
    try {
      if (this.courseToEdit.sections[i].modules[j].classes.length - 1 === 0) return;

      const sectionNumber = this.getSectionDisplayNumber(i);
      const moduleNumber = j + 1;
      const classNumber = k + 1;
      const body: any = {
        courseId: this.courseToEdit.courseId,
        courseSectionId: this.courseToEdit.sections[i].courseSectionId,
        courseModuleId: this.courseToEdit.sections[i].modules[j].courseModuleId,
        courseClassId: this.courseToEdit.sections[i].modules[j].classes[k].courseClassId,
        sectionNumber,
        moduleNumber,
        classNumber
      };

      const response = await firstValueFrom(this.api.deleteCourseClass(body));

      this.courseToEdit.sections[i].modules[j].classes.splice(k, 1);
      this.getClassesFormArray(i, j).removeAt(k);

      if (this.currentAttachments.sections[i]?.modules[j]?.classes[k]) {
        this.currentAttachments.sections[i].modules[j].classes.splice(k, 1);
      }

      this.toast.presentSuccessToast(response.message);
    } catch (errorResponse: any) {
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando clase');
    }
  }

  onDroppedSectionResources(files: Array<CustomFile>, i: number): void {
    if (!files?.length) return;

    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }

    this.courseToEdit.sections[i].filesToUpload = files;

    const sectionControl = this.getSectionFormGroup(i);
    sectionControl.markAsDirty();
  }

  onDroppedClassResources(files: Array<CustomFile>, i: number, j: number, k: number): void {
    if (!files?.length) return;

    for (const file of files) {
      file.name = this.utils.generateFileName(file.name);
    }

    this.courseToEdit.sections[i].modules[j].classes[k].filesToUpload = files;

    const classControl = this.getClassFormGroup(i, j, k);
    classControl.markAsDirty();
  }

  onSectionLocalFilesUpdated(files: CustomFile[], i: number): void {
    this.courseToEdit.sections[i].filesToUpload = files;
    const sectionControl = this.getSectionFormGroup(i);
    sectionControl.markAsDirty();
  }

  onClassLocalFilesUpdated(files: CustomFile[], i: number, j: number, k: number): void {
    this.courseToEdit.sections[i].modules[j].classes[k].filesToUpload = files;
    const classControl = this.getClassFormGroup(i, j, k);
    classControl.markAsDirty();
  }

  trimTextValues(obj: any): any {
    if (!obj) return obj;

    if (typeof obj === 'string') {
      return obj.trim();
    }

    if (Array.isArray(obj)) {
      return obj.map((item) => this.trimTextValues(item));
    }

    if (typeof obj === 'object') {
      const result: any = {};
      for (const key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
          result[key] = this.trimTextValues(obj[key]);
        }
      }
      return result;
    }

    return obj;
  }

  convertVimeoIdsToUrls(formValue: any): any {
    if (!formValue) return formValue;

    const processedFormValue = cloneDeep(formValue);

    if (processedFormValue.sections && Array.isArray(processedFormValue.sections)) {
      processedFormValue.sections = processedFormValue.sections.reduce((sections: any[], section: any) => {
        if (section.modules && Array.isArray(section.modules)) {
          const newSection = {
            ...section,
            modules: section.modules.reduce((modules: any[], module: any) => {
              if (module.classes && Array.isArray(module.classes)) {
                const newModule = {
                  ...module,
                  classes: module.classes.reduce((classes: any[], classItem: any) => {
                    const newClassItem = { ...classItem };

                    if (newClassItem.classVideoUrl) {
                      const embedUrl = getVimeoEmbedUrl(newClassItem.classVideoUrl);
                      if (embedUrl) {
                        newClassItem.classVideoUrl = embedUrl;
                      }
                    }

                    return [...classes, newClassItem];
                  }, [])
                };
                return [...modules, newModule];
              } else {
                return [...modules, { ...module }];
              }
            }, [])
          };
          return [...sections, newSection];
        } else {
          return [...sections, { ...section }];
        }
      }, []);
    }

    return processedFormValue;
  }

  convertVimeoUrlsToIds(courseData: CourseToEdit): CourseToEdit {
    if (!courseData) return courseData;

    const processedCourse = cloneDeep(courseData);

    if (processedCourse.sections && Array.isArray(processedCourse.sections)) {
      processedCourse.sections = processedCourse.sections.reduce((sections: SectionToEdit[], section: SectionToEdit) => {
        if (section.modules && Array.isArray(section.modules)) {
          const newSection: SectionToEdit = {
            ...section,
            modules: section.modules.reduce((modules: ModuleToEdit[], module: ModuleToEdit) => {
              if (module.classes && Array.isArray(module.classes)) {
                const newModule: ModuleToEdit = {
                  ...module,
                  classes: module.classes.reduce((classes: ClassToEdit[], classItem: ClassToEdit) => {
                    const classVideoUrl =
                      classItem.classVideoUrl && typeof classItem.classVideoUrl === 'string'
                        ? (extractVimeoVideoId(classItem.classVideoUrl) ?? '')
                        : '';

                    return [
                      ...classes,
                      {
                        ...classItem,
                        classVideoUrl
                      }
                    ];
                  }, [])
                };
                return [...modules, newModule];
              } else {
                return [...modules, module];
              }
            }, [])
          };
          return [...sections, newSection];
        } else {
          return [...sections, section];
        }
      }, []);
    }

    return processedCourse;
  }

  // Calendar image handlers
  onCalendarFileDropped(files: CustomFile[]): void {
    if (files && files.length > 0) {
      this.calendarLocalFile = files[0];
      this.calendarImageAction = 'keep';
      this.courseForm.markAsDirty();
    }
  }

  onCalendarLocalFileDeleted(): void {
    this.calendarLocalFile = null;
  }

  onCalendarCurrentFileDeleted(): void {
    this.calendarCurrentFile = null;
    this.calendarImageAction = 'delete';
    this.courseForm.markAsDirty();
  }

  async confirmDeleteCalendar(): Promise<void> {
    const header = 'Eliminar calendario';
    const message = '¿Estás seguro(a) de eliminar el calendario del curso?';
    const noDelete = 'Cancelar';
    const remove = 'Eliminar';
    const confirmDelete = await this.alert.presentConfirmRemoveActionAlert({
      title: header,
      message,
      cancelText: noDelete,
      confirmText: remove
    });
    if (confirmDelete) {
      this.calendarLocalFile = null;
      this.calendarCurrentFile = null;
      this.calendarImageAction = 'delete';
      this.courseForm.markAsDirty();
    }
  }

  async openCalendarPreview(event: { url: string; isPdf: boolean; fileName: string }): Promise<void> {
    this.isCalendarPreviewOpen = true;

    try {
      const dialogRef = await this.modal.openCalendarModal(event.url, event.isPdf, event.fileName);

      if (dialogRef) {
        await firstValueFrom(dialogRef.afterClosed());
      }
    } finally {
      this.isCalendarPreviewOpen = false;
    }
  }

  async saveCourseContent(): Promise<void> {
    try {
      // A Clase 0 is saved after the main content (see below), so validate it up front: otherwise an invalid one
      // would be silently skipped after everything else was persisted, still reporting success.
      const dirtyClassZeroes = this.classZeroComponents?.filter((c) => c.isDirty) ?? [];
      const invalidClassZero = dirtyClassZeroes.filter((c) => !c.validate());
      if (invalidClassZero.length) {
        this.toast.presentErrorToast('No se ha guardado: la clase introductoria tiene campos requeridos sin completar o inválidos');
        setTimeout(() => invalidClassZero[0].scrollIntoView(), 300);
        return;
      }

      this.isSavingCourse = true;

      // Save any dirty notes components before saving the main form
      const dirtyNotes = this.notesComponents?.filter((c) => c.isDirty) ?? [];
      if (dirtyNotes.length) {
        await Promise.all(dirtyNotes.map((c) => c.saveChanges()));
      }
      this.hasUnsavedNotes = false;

      // Save any pending quiz excluded-state changes
      const dirtyQuizzes = this.quizComponents?.filter((c) => c.isDirty) ?? [];
      if (dirtyQuizzes.length) {
        await Promise.all(dirtyQuizzes.map((c) => c.saveChanges()));
      }

      const formValue = this.courseForm.value;
      const trimmedFormValue = this.trimTextValues(formValue);
      const processedFormValue = this.convertVimeoIdsToUrls(trimmedFormValue);

      const courseFormData = this.createCourseFormData(processedFormValue);

      await firstValueFrom(this.api.saveCourseContent(courseFormData));

      // Clase 0 (ENF-2314) saves after the main content: a brand-new year's courseYearId only
      // exists in course_year once the call above completes, and Clase 0's FK depends on it.
      const classZeroResults = await Promise.all(dirtyClassZeroes.map((c) => c.saveChanges()));
      this.isSavingCourse = false;
      if (classZeroResults.includes(false)) {
        // The Clase 0 component already showed its own error toast; stay on the page so the admin can retry.
        return;
      }
      this.hasUnsavedClassZero = false;

      this.toast.presentSuccessToast(`Se ha modificado correctamente el contenido del curso ${this.courseName}`);

      this.goToList();
    } catch (errorResponse: any) {
      this.isSavingCourse = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error guardando curso');
    }
  }

  onNotesDirtyChange(isDirty: boolean): void {
    this.hasUnsavedNotes = isDirty || (this.notesComponents?.some((c) => c.isDirty) ?? false);
  }

  onQuizDirtyChange(isDirty: boolean): void {
    this.hasUnsavedQuizzes = isDirty || (this.quizComponents?.some((c) => c.isDirty) ?? false);
  }

  onClassZeroDirtyChange(isDirty: boolean): void {
    this.hasUnsavedClassZero = isDirty || (this.classZeroComponents?.some((c) => c.isDirty) ?? false);
  }

  onNotesReviewStateChanged(classGroup: AbstractControl, isPending: boolean): void {
    classGroup.get('notesPendingReview')?.setValue(isPending, { emitEvent: false });
  }

  onQuizReviewStateChanged(classGroup: AbstractControl, isPending: boolean): void {
    classGroup.get('quizPendingReview')?.setValue(isPending, { emitEvent: false });
  }

  onClassProcessingChange(classId: string, isProcessing: boolean): void {
    if (isProcessing) {
      this.processingClassIds.add(classId);
    } else {
      this.processingClassIds.delete(classId);
    }
    this.processingClassIds = new Set(this.processingClassIds);
  }

  private createCourseFormData(processedFormValue: any): FormData {
    const courseFormData = new FormData();

    const formDataWithFiles = this.courseToEdit.sections.reduce((formData, section) => {
      return this.processSection(formData, section);
    }, courseFormData);

    // Add calendar image if present
    if (this.calendarLocalFile && this.calendarLocalFile.file) {
      formDataWithFiles.append('calendarImage', this.calendarLocalFile.file, this.calendarLocalFile.name);
    }

    const cleanCourseData = this.createCleanCourseData(processedFormValue);

    // Include calendarImageAction in the body JSON
    const bodyWithCalendar = {
      ...cleanCourseData,
      calendarImageAction: this.calendarImageAction
    };

    formDataWithFiles.append('form', JSON.stringify(bodyWithCalendar));

    return formDataWithFiles;
  }

  private processSection(formData: FormData, section: any): FormData {
    const formDataWithSectionFiles = section.filesToUpload?.length
      ? section.filesToUpload.reduce((fd: FormData, fileValue: any) => {
          fd.append(`section_${section.courseSectionId}`, fileValue.file, fileValue.name);
          return fd;
        }, formData)
      : formData;

    return section.modules.reduce((fd: FormData, module: any) => {
      return module.classes.reduce((classFormData: FormData, classValue: any) => {
        return classValue.filesToUpload?.length
          ? classValue.filesToUpload.reduce((fileFormData: FormData, fileValue: any) => {
              fileFormData.append(
                `section_${section.courseSectionId}_module_${module.courseModuleId}_class_${classValue.courseClassId}`,
                fileValue.file,
                fileValue.name
              );
              return fileFormData;
            }, classFormData)
          : classFormData;
      }, fd);
    }, formDataWithSectionFiles);
  }

  private createCleanCourseData(formValue: any): any {
    const fieldsToRemove = ['acceptClassFilesLimit', 'filesToUpload', 'isDeletingRemoteAttachment'];

    return {
      ...formValue,
      // Read publishDate directly from the form control, same reason as modules below:
      // trimTextValues would destroy the Luxon DateTime by converting it to a plain object.
      years: (formValue.years ?? []).map((year: any, yearIndex: number) => {
        const rawPublishDate = this.yearsFormArray.at(yearIndex)?.get('publishDate')?.value ?? null;
        let publishDate: string | null = null;
        if (rawPublishDate && typeof rawPublishDate === 'object' && typeof rawPublishDate.toISO === 'function') {
          publishDate = rawPublishDate.isValid ? rawPublishDate.toISO() : null;
        } else if (rawPublishDate instanceof Date) {
          publishDate = isNaN(rawPublishDate.getTime()) ? null : rawPublishDate.toISOString();
        }
        const hidden = this.yearsFormArray.at(yearIndex)?.get('hidden')?.value ?? false;
        return { courseYearId: year.courseYearId, hidden, publishDate };
      }),
      sections: formValue.sections.reduce((cleanSections: any[], section: any, sectionIndex: number) => {
        const cleanSection = this.removeFields(section, fieldsToRemove);

        // Include number property from courseToEdit
        const originalSection = this.courseToEdit.sections[sectionIndex];
        if (originalSection?.number !== undefined) {
          cleanSection.number = originalSection.number;
        }

        if (cleanSection.modules) {
          cleanSection.modules = cleanSection.modules.reduce((cleanModules: any[], module: any, moduleIndex: number) => {
            const cleanModule = { ...module };

            // Read publishDate directly from the form control to avoid trimTextValues
            // destroying the Luxon DateTime by converting it to a plain object
            const sectionsArray = this.courseForm.get('sections') as FormArray;
            const rawPublishDate = (sectionsArray.at(sectionIndex).get('modules') as FormArray)?.at(moduleIndex)?.get('publishDate')?.value ?? null;
            if (rawPublishDate && typeof rawPublishDate === 'object' && typeof rawPublishDate.toISO === 'function') {
              // Luxon DateTime
              cleanModule.publishDate = rawPublishDate.isValid ? rawPublishDate.toISO() : null;
            } else if (rawPublishDate instanceof Date) {
              cleanModule.publishDate = isNaN(rawPublishDate.getTime()) ? null : rawPublishDate.toISOString();
            } else {
              cleanModule.publishDate = null;
            }

            // Include number property from courseToEdit
            const originalModule = originalSection?.modules?.[moduleIndex];
            if (originalModule?.number !== undefined) {
              cleanModule.number = originalModule.number;
            }

            if (cleanModule.classes) {
              cleanModule.classes = cleanModule.classes.reduce((cleanClasses: any[], courseClass: any, classIndex: number) => {
                const cleanClass = this.removeFields(courseClass, fieldsToRemove);
                // _contentLoaded is read by the backend to decide which fields to update.
                // If the class was never expanded, strip all content fields from the payload
                // so the backend preserves their existing DB values.
                if (!courseClass._contentLoaded) {
                  delete cleanClass.classVideoUrl;
                  delete cleanClass.description;
                  delete cleanClass.attachments;
                  delete cleanClass.hidden;
                }

                // Include number property from courseToEdit
                const originalClass = originalModule?.classes?.[classIndex];
                if (originalClass?.number !== undefined) {
                  cleanClass.number = originalClass.number;
                }

                return [...cleanClasses, cleanClass];
              }, []);
            }

            return [...cleanModules, cleanModule];
          }, []);
        }

        return [...cleanSections, cleanSection];
      }, [])
    };
  }

  private removeFields(obj: any, fieldsToRemove: string[]): any {
    return Object.keys(obj).reduce((cleanObj: any, key: string) => {
      if (!fieldsToRemove.includes(key)) {
        cleanObj[key] = obj[key];
      }
      return cleanObj;
    }, {});
  }

  async onDeleteRemoteSectionAttachment(file: CustomUploadedFile, i: number): Promise<void> {
    try {
      const attachment = {
        name: file.filename,
        url: file.path
      };

      const header = 'Eliminar archivo';
      const message = '¿Estás seguro(a) de eliminar este archivo? Esta acción no se podrá deshacer.';
      const cancel = 'Cancelar';
      const confirm = 'Eliminar';
      const deleteConfirmed = await this.alert.presentConfirmActionAlert(header, message, cancel, confirm);

      if (!deleteConfirmed) return;

      this.courseToEdit.sections[i].isDeletingRemoteAttachment = true;
      const courseSectionId = this.courseToEdit.sections[i].courseSectionId;
      const body = {
        courseId: this.courseToEdit.courseId,
        courseSectionId,
        attachment
      };

      const response = await firstValueFrom(this.api.deleteSectionAttachment(body));
      this.courseToEdit.sections[i].isDeletingRemoteAttachment = false;

      const fileIndex = this.currentAttachments.sections[i].attachments.findIndex((a) => a.filename === file.filename && a.path === file.path);

      if (fileIndex !== -1) {
        this.currentAttachments.sections[i].attachments.splice(fileIndex, 1);
      }

      const attachmentIndex = this.courseToEdit.sections[i].attachments.findIndex((a) => a.name === file.filename && a.url === file.path);

      if (attachmentIndex !== -1) {
        this.courseToEdit.sections[i].attachments.splice(attachmentIndex, 1);
      }

      this.toast.presentSuccessToast(response.message || 'Archivo eliminado correctamente');
    } catch (errorResponse: any) {
      this.courseToEdit.sections[i].isDeletingRemoteAttachment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando adjunto');
    }
  }

  async onDeleteRemoteClassAttachment(file: CustomUploadedFile, i: number, j: number, k: number): Promise<void> {
    try {
      const attachment = {
        name: file.filename,
        url: file.path
      };

      const header = 'Eliminar archivo';
      const message = '¿Estás seguro(a) de eliminar este archivo? Esta acción no se podrá deshacer.';
      const cancel = 'Cancelar';
      const confirm = 'Eliminar';
      const deleteConfirmed = await this.alert.presentConfirmActionAlert(header, message, cancel, confirm);

      if (!deleteConfirmed) return;

      this.courseToEdit.sections[i].modules[j].classes[k].isDeletingRemoteAttachment = true;
      const courseClassId = this.courseToEdit.sections[i].modules[j].classes[k].courseClassId;
      const body = {
        courseId: this.courseToEdit.courseId,
        courseClassId,
        attachment
      };

      const response = await firstValueFrom(this.api.deleteClassAttachment(body));
      this.courseToEdit.sections[i].modules[j].classes[k].isDeletingRemoteAttachment = false;

      const fileIndex = this.currentAttachments.sections[i].modules[j].classes[k].attachments.findIndex(
        (a) => a.filename === file.filename && a.path === file.path
      );

      if (fileIndex !== -1) {
        this.currentAttachments.sections[i].modules[j].classes[k].attachments.splice(fileIndex, 1);
      }

      const attachmentIndex = this.courseToEdit.sections[i].modules[j].classes[k].attachments.findIndex(
        (a) => a.name === file.filename && a.url === file.path
      );

      if (attachmentIndex !== -1) {
        this.courseToEdit.sections[i].modules[j].classes[k].attachments.splice(attachmentIndex, 1);
      }

      this.toast.presentSuccessToast(response.message || 'Archivo eliminado correctamente');
    } catch (errorResponse: any) {
      this.courseToEdit.sections[i].modules[j].classes[k].isDeletingRemoteAttachment = false;
      const errorMessage = errorResponse?.error?.message;
      this.toast.presentErrorToast(errorMessage || 'Hubo un error borrando adjunto');
    }
  }

  onDropSectionFilesClick(filesOrIndex: CustomFile[] | number, i?: number): void {
    if (typeof filesOrIndex === 'number') {
      this.toast.presentSuccessToast('Selecciona archivos para subir a esta asignatura');
      return;
    }

    const files = filesOrIndex;
    const sectionIndex = i as number;

    if (!files?.length) return;

    this.onDroppedSectionResources(files, sectionIndex);
    this.toast.presentSuccessToast(`Se ha añadido ${files.length} archivo(s) a la asignatura ${sectionIndex + 1}`);
  }

  mapAttachmentsToCurrentFiles(i: number): CustomUploadedFile[] {
    const attachments = this.courseToEdit.sections[i].attachments || [];
    if (!attachments || !attachments.length) {
      return [];
    }

    return attachments.map((attachment: any) => ({
      filename: attachment.name,
      path: attachment.url
    }));
  }

  onDropClassFilesClick(filesOrIndex: CustomFile[] | number, i: number, j?: number, k?: number): void {
    if (typeof filesOrIndex === 'number') {
      this.toast.presentSuccessToast('Selecciona archivos para subir a esta clase');
      return;
    }

    const files = filesOrIndex;
    const sectionIndex = i;
    const moduleIndex = j as number;
    const classIndex = k as number;

    if (!files?.length) return;

    this.onDroppedClassResources(files, sectionIndex, moduleIndex, classIndex);
    this.toast.presentSuccessToast(`Se ha añadido ${files.length} archivo(s) a la clase ${classIndex + 1}`);
  }

  get sectionsFormArray(): FormArray {
    return this.courseForm.get('sections') as FormArray;
  }

  get yearsFormArray(): FormArray {
    return this.courseForm.get('years') as FormArray;
  }

  getYearFormGroup(yearIndex: number): FormGroup {
    return this.yearsFormArray.at(yearIndex) as FormGroup;
  }

  clearYearPublishDate(yearIndex: number): void {
    const publishDateControl = this.getYearFormGroup(yearIndex).get('publishDate');
    if (publishDateControl) {
      publishDateControl.setValue(null);
      publishDateControl.markAsDirty();
      publishDateControl.markAsTouched();
    }
  }

  onYearHiddenChange(yearIndex: number, checked: boolean): void {
    const yearControl = this.getYearFormGroup(yearIndex);
    const hiddenControl = yearControl.get('hidden');

    if (hiddenControl) {
      const initialValue = this.courseToEdit.years?.[yearIndex]?.hidden || false;

      hiddenControl.setValue(checked);
      hiddenControl.markAsTouched();

      if (checked !== initialValue) {
        hiddenControl.markAsDirty();
      } else {
        hiddenControl.markAsPristine();
      }
    }
  }

  getSectionFormGroup(i: number): FormGroup {
    return this.sectionsFormArray.at(i) as FormGroup;
  }

  getModulesFormArray(i: number): FormArray {
    return this.getSectionFormGroup(i).get('modules') as FormArray;
  }

  getModuleFormGroup(i: number, j: number): FormGroup {
    return this.getModulesFormArray(i).at(j) as FormGroup;
  }

  hasModuleNotesPendingReview(i: number, j: number): boolean {
    return this.getClassesFormArray(i, j).controls.some((c) => c.value.notesPendingReview);
  }

  hasModuleQuizPendingReview(i: number, j: number): boolean {
    return this.getClassesFormArray(i, j).controls.some((c) => c.value.quizPendingReview);
  }

  hasSectionNotesPendingReview(i: number): boolean {
    return this.getModulesFormArray(i).controls.some((_, j) => this.hasModuleNotesPendingReview(i, j));
  }

  private findClassFormGroupById(courseClassId: string): FormGroup | null {
    if (!this.courseForm) return null;
    for (let i = 0; i < this.sectionsFormArray.length; i++) {
      const modules = this.getModulesFormArray(i);
      for (let j = 0; j < modules.length; j++) {
        const classes = this.getClassesFormArray(i, j);
        for (let k = 0; k < classes.length; k++) {
          const group = classes.at(k) as FormGroup;
          if (group.value.courseClassId === courseClassId) return group;
        }
      }
    }
    return null;
  }

  getClassesFormArray(i: number, j: number): FormArray {
    return this.getModuleFormGroup(i, j).get('classes') as FormArray;
  }

  getClassFormGroup(i: number, j: number, k: number): FormGroup {
    return this.getClassesFormArray(i, j).at(k) as FormGroup;
  }

  getMessageClassesFormArray(i: number, j: number, k: number): FormArray {
    return this.getClassFormGroup(i, j, k).get('messages') as FormArray;
  }

  getMessageClassFormGroup(i: number, j: number, k: number, m: number): FormGroup {
    return this.getMessageClassesFormArray(i, j, k).at(m) as FormGroup;
  }

  getSectionAttachments(sectionIndex: number): CustomUploadedFile[] {
    return this.currentAttachments?.sections?.[sectionIndex]?.attachments || [];
  }

  getClassAttachments(sectionIndex: number, moduleIndex: number, classIndex: number): CustomUploadedFile[] {
    return this.currentAttachments?.sections?.[sectionIndex]?.modules?.[moduleIndex]?.classes?.[classIndex]?.attachments || [];
  }

  isModuleExpanded(sectionIndex: number, moduleIndex: number): boolean {
    return this.expandedModules[sectionIndex]?.[moduleIndex] || false;
  }

  isClassExpanded(sectionIndex: number, moduleIndex: number, classIndex: number): boolean {
    return this.expandedClasses[sectionIndex]?.[moduleIndex]?.[classIndex] || false;
  }

  togglePromotion(i: number, j: number, event: any): void {
    const moduleForm = this.getModuleFormGroup(i, j);
    const showPromotionControl = moduleForm.get('showPromotion');

    if (showPromotionControl) {
      const isShowingPromotion = event.checked;
      const initialValue = this.courseToEdit.sections[i].modules[j].showPromotion || false;

      showPromotionControl.setValue(isShowingPromotion);
      showPromotionControl.markAsTouched();

      if (isShowingPromotion !== initialValue) {
        showPromotionControl.markAsDirty();
      } else {
        showPromotionControl.markAsPristine();
      }

      const promotionFields = ['promotionTitle', 'promotionDescription', 'promotionButtonText'];
      const promotionButtonUrlField = 'promotionButtonUrl';
      promotionFields.forEach((fieldName) => {
        const control = moduleForm.get(fieldName);
        if (control) {
          if (isShowingPromotion) {
            control.setValidators(CourseContentComponent.requiredNoWhitespace());
          } else {
            control.clearValidators();
          }
          control.updateValueAndValidity({ emitEvent: false });
          control.markAsPristine();
          control.markAsUntouched();
        }
      });

      const urlControl = moduleForm.get(promotionButtonUrlField);
      if (urlControl) {
        if (isShowingPromotion) {
          urlControl.setValidators([Validators.required, CourseContentComponent.noWhitespaceValidator, Validators.pattern(/^https?:\/\/[^\s]+$/)]);
        } else {
          urlControl.clearValidators();
        }
        urlControl.updateValueAndValidity({ emitEvent: false });
        urlControl.markAsPristine();
        urlControl.markAsUntouched();
      }
    }
  }

  toggleCheckbox(i: number, j: number): void {
    const moduleForm = this.getModuleFormGroup(i, j);
    const showPromotionControl = moduleForm.get('showPromotion');

    if (showPromotionControl) {
      const newValue = !showPromotionControl.value;
      showPromotionControl.setValue(newValue);

      const event = { checked: newValue };
      this.togglePromotion(i, j, event);
    }
  }

  onSectionHiddenChange(i: number, checked: boolean): void {
    const sectionControl = this.getSectionFormGroup(i);
    const hiddenControl = sectionControl.get('hidden');

    if (hiddenControl) {
      const initialValue = this.courseToEdit.sections[i].hidden || false;

      hiddenControl.setValue(checked);
      hiddenControl.markAsTouched();

      if (checked !== initialValue) {
        hiddenControl.markAsDirty();
      } else {
        hiddenControl.markAsPristine();
      }
    }
  }

  onModuleHiddenChange(i: number, j: number, checked: boolean): void {
    const moduleControl = this.getModuleFormGroup(i, j);
    const hiddenControl = moduleControl.get('hidden');

    if (hiddenControl) {
      const initialValue = this.courseToEdit.sections[i].modules[j].hidden || false;

      hiddenControl.setValue(checked);
      hiddenControl.markAsTouched();

      if (checked !== initialValue) {
        hiddenControl.markAsDirty();
      } else {
        hiddenControl.markAsPristine();
      }
    }
  }

  clearModulePublishDate(i: number, j: number): void {
    const moduleControl = this.getModuleFormGroup(i, j);
    const publishDateControl = moduleControl.get('publishDate');
    if (publishDateControl) {
      publishDateControl.setValue(null);
      publishDateControl.markAsDirty();
      publishDateControl.markAsTouched();
    }
  }

  onClassHiddenChange(i: number, j: number, k: number, checked: boolean): void {
    const classControl = this.getClassFormGroup(i, j, k);
    const hiddenControl = classControl.get('hidden');

    if (hiddenControl) {
      const initialValue = this.courseToEdit.sections[i].modules[j].classes[k].hidden || false;

      hiddenControl.setValue(checked);
      hiddenControl.markAsTouched();

      if (checked !== initialValue) {
        hiddenControl.markAsDirty();
      } else {
        hiddenControl.markAsPristine();
      }
    }
  }

  readonly notesErrorLabels: Record<string, string> = {
    not_found: 'Clase no encontrada',
    no_transcript: 'Sin transcripción',
    empty_transcript: 'Transcripción vacía',
    ai_error: 'Error del modelo IA',
    pdf_error: 'Error generando PDF',
    s3_error: 'Error subiendo a S3',
    unknown: 'Error desconocido'
  };

  async toggleNotesPublished(i: number, j: number, k: number, courseClassId: string, currentPublished: boolean): Promise<void> {
    const classControl = this.getClassFormGroup(i, j, k);
    classControl.patchValue({ isTogglingPublish: true });
    try {
      const newValue = !currentPublished;
      await firstValueFrom(this.api.toggleClassNotesPublished({ courseClassId, notesPublished: newValue }));
      classControl.patchValue({ notesPublished: newValue, isTogglingPublish: false });
      this.chRef.markForCheck();
    } catch {
      classControl.patchValue({ isTogglingPublish: false });
      this.toast.presentErrorToast('No se pudo actualizar el estado de publicación de los apuntes.');
      this.chRef.markForCheck();
    }
  }

  onVimeoIdPaste(event: ClipboardEvent, classGroup: AbstractControl): void {
    event.preventDefault();

    const vimeoId = extractVimeoIdFromClipboardEvent(event);
    const classVideoUrlControl = classGroup.get('classVideoUrl');
    if (classVideoUrlControl) {
      classVideoUrlControl.setValue(vimeoId);
      classVideoUrlControl.markAsDirty();
    }
  }

  isSectionInvalid(sectionIndex: number): boolean {
    // Guards against a transient out-of-range index while CDK's drag-drop reorders
    // sectionsFormArray and the `@for`/template context haven't settled on the new
    // indices yet in the same tick — without this, `.invalid` on undefined throws and
    // aborts change detection mid-render, leaving the drag's CSS transform stuck.
    const sectionForm = this.getSectionFormGroup(sectionIndex);
    return sectionForm?.invalid ?? false;
  }

  isModuleInvalid(sectionIndex: number, moduleIndex: number): boolean {
    // Same transient out-of-range guard as isSectionInvalid, one level deeper: getModuleFormGroup
    // calls .get('modules') directly on getSectionFormGroup's result, which throws instead of
    // returning undefined during the same drag-drop race — so it can't be called here directly.
    const modulesArray = this.getSectionFormGroup(sectionIndex)?.get('modules') as FormArray | undefined;
    const moduleForm = modulesArray?.at(moduleIndex);
    return moduleForm?.invalid ?? false;
  }

  isClassInvalid(sectionIndex: number, moduleIndex: number, classIndex: number): boolean {
    // Same guard as isModuleInvalid above, one level deeper still.
    const modulesArray = this.getSectionFormGroup(sectionIndex)?.get('modules') as FormArray | undefined;
    const classesArray = modulesArray?.at(moduleIndex)?.get('classes') as FormArray | undefined;
    const classForm = classesArray?.at(classIndex);
    if (!classForm) return false;
    // Unexpanded classes have no validators yet; treat them as valid in the header
    if (!classForm.get('_contentLoaded')?.value) return false;
    return classForm.invalid;
  }

  // Drag & Drop methods
  /**
   * Reorders a section within its drop list. For standard/flash courses (no `courseYearId`) the
   * drop list holds every section, so `event.previousIndex`/`currentIndex` are already the flat
   * `courseToEdit.sections` indices used everywhere else. For FP courses each "Año" panel owns its
   * own, unconnected `cdkDropList` (see the template's `sectionPanelTpl` usage), so CDK's indices
   * are local to that year's subset — `getYearSectionIndices` maps them back to the flat indices
   * before reusing the exact same reorder logic. Years being separate, unconnected drop lists also
   * means CDK itself refuses a cross-year drag (no manual guard needed here anymore).
   */
  dropSection(event: CdkDragDrop<FormArray>, courseYearId?: string): void {
    if (event.previousIndex === event.currentIndex) return;

    let previousIndex = event.previousIndex;
    let currentIndex = event.currentIndex;

    if (courseYearId !== undefined) {
      const yearIndices = this.getYearSectionIndices(courseYearId);
      previousIndex = yearIndices[event.previousIndex];
      currentIndex = yearIndices[event.currentIndex];
    }

    const sectionsArray = this.sectionsFormArray;
    const movedSection = sectionsArray.at(previousIndex);

    // Reorder in FormArray
    sectionsArray.removeAt(previousIndex);
    sectionsArray.insert(currentIndex, movedSection);

    // Reorder in courseToEdit
    moveItemInArray(this.courseToEdit.sections, previousIndex, currentIndex);

    // Update expansion states
    moveItemInArray(this.expandedSections, previousIndex, currentIndex);
    moveItemInArray(this.expandedModules, previousIndex, currentIndex);
    moveItemInArray(this.expandedClasses, previousIndex, currentIndex);
    moveItemInArray(this.currentAttachments.sections, previousIndex, currentIndex);
    moveItemInArray(this.fetchedUrls.sections, previousIndex, currentIndex);
    moveItemInArray(this.fetchedUrls.classes, previousIndex, currentIndex);

    // Mark form as dirty to enable save button
    this.courseForm.markAsDirty();
    this.courseForm.markAsTouched();
    this.courseForm.updateValueAndValidity();
  }

  dropModule(event: CdkDragDrop<FormArray>, sectionIndex: number): void {
    if (event.previousIndex === event.currentIndex) return;

    const modulesArray = this.getModulesFormArray(sectionIndex);
    const movedModule = modulesArray.at(event.previousIndex);

    // Reorder in FormArray
    modulesArray.removeAt(event.previousIndex);
    modulesArray.insert(event.currentIndex, movedModule);

    // Reorder in courseToEdit
    moveItemInArray(this.courseToEdit.sections[sectionIndex].modules, event.previousIndex, event.currentIndex);

    // Update expansion states
    moveItemInArray(this.expandedModules[sectionIndex], event.previousIndex, event.currentIndex);
    moveItemInArray(this.expandedClasses[sectionIndex], event.previousIndex, event.currentIndex);
    moveItemInArray(this.currentAttachments.sections[sectionIndex].modules, event.previousIndex, event.currentIndex);

    // Mark form as dirty to enable save button
    this.courseForm.markAsDirty();
    this.courseForm.markAsTouched();
    this.courseForm.updateValueAndValidity();
  }

  dropClass(event: CdkDragDrop<FormArray>, sectionIndex: number, moduleIndex: number): void {
    if (event.previousIndex === event.currentIndex) return;

    const classesArray = this.getClassesFormArray(sectionIndex, moduleIndex);
    const movedClass = classesArray.at(event.previousIndex);

    // Reorder in FormArray
    classesArray.removeAt(event.previousIndex);
    classesArray.insert(event.currentIndex, movedClass);

    // Reorder in courseToEdit
    moveItemInArray(this.courseToEdit.sections[sectionIndex].modules[moduleIndex].classes, event.previousIndex, event.currentIndex);

    // Update expansion states
    moveItemInArray(this.expandedClasses[sectionIndex][moduleIndex], event.previousIndex, event.currentIndex);
    moveItemInArray(this.currentAttachments.sections[sectionIndex].modules[moduleIndex].classes, event.previousIndex, event.currentIndex);

    // Mark form as dirty to enable save button
    this.courseForm.markAsDirty();
    this.courseForm.markAsTouched();
    this.courseForm.updateValueAndValidity();
  }

  /**
   * Closes every OTHER section accordion when a drag starts — leaves the dragged section's own
   * panel state untouched. Collapsing it too (as this used to do, unconditionally) destroys its
   * lazily-rendered body content mid-drag (Angular Material only keeps an expanded panel's content
   * in the DOM while it's open), shrinking the panel CDK is actively dragging and throwing off its
   * position tracking — the dragged item then doesn't settle where it was dropped.
   */
  onSectionDragStarted(sectionIndex: number): void {
    this.expandedSections = this.expandedSections.map((expanded, i) => (i === sectionIndex ? expanded : false));
  }

  onModuleDragStarted(sectionIndex: number): void {
    // Close all module accordions in this section when drag starts
    this.expandedModules[sectionIndex] = this.expandedModules[sectionIndex].map(() => false);
  }

  onClassDragStarted(sectionIndex: number, moduleIndex: number): void {
    // Close all class accordions in this module when drag starts
    this.expandedClasses[sectionIndex][moduleIndex] = this.expandedClasses[sectionIndex][moduleIndex].map(() => false);
  }

  private async loadMessages(): Promise<void> {
    try {
      this.isLoadingCourse = true;

      const response = await firstValueFrom(
        this.api.fetchMessages({
          title: '',
          active: true,
          sendCondition: MessageSendCondition.OnCourseClassCompletion
        })
      );

      this.messages = response.data.messages || [];
      const messagesWithState = this.messages.map((msg: MessageDto) => ({ ...msg, switchControl: undefined }));
      this.messageOptions = messagesWithState.map((msg: MessageDto) => ({ viewValue: msg.title, value: msg.messageId }));
    } catch (error: any) {
      const errorMessage = error?.error?.message || 'Error al cargar los mensajes';
      this.toast.presentErrorToast(errorMessage);

      this.messageOptions = [];
    } finally {
      this.isLoadingCourse = false;
    }
  }

  hasClassMessageEnabled(i: number, j: number, k: number): boolean {
    const classMessages = this.getMessageClassesFormArray(i, j, k);
    for (let m = 0; m < classMessages.length; m++) {
      const messageGroup = this.getMessageClassFormGroup(i, j, k, m);
      const enabledControl = messageGroup.get('enabled');
      if (enabledControl && enabledControl.value === true) {
        return true;
      }
    }
    return false;
  }

  setMovingClassId(classId: string | null, moduleId: string | null, sectionId: string | null): void {
    const sectionIdControl = this.movingClassForm.get('sectionId');
    const previousSubscription = sectionIdControl?.valueChanges.subscribe(() => {
      this.movingClassForm.get('moduleId')?.setValue(null);
    });
    previousSubscription?.unsubscribe();
    this.movingClassForm.setValue(
      {
        classId,
        moduleId,
        sectionId
      },
      { emitEvent: false }
    );
    this.movingClassForm.markAsDirty();
    this.chRef.detectChanges();
  }

  setMovingModuleId(moduleId: string | null, sectionId: string | null): void {
    this.movingModuleForm.patchValue({
      moduleId,
      sectionId
    });
    this.movingModuleForm.markAsDirty();
    this.chRef.detectChanges();
  }

  moveClass(): void {
    // if movingClassForm is valid move class to section and module
    if (this.movingClassForm.valid) {
      const { classId, moduleId, sectionId } = this.movingClassForm.value;

      // Find the class to move
      let classToMove: ClassToEdit | null = null;
      let fromSectionIndex = -1;
      let fromModuleIndex = -1;
      let fromClassIndex = -1;

      // Search for the class in the current structure
      for (let i = 0; i < this.courseToEdit.sections.length; i++) {
        for (let j = 0; j < this.courseToEdit.sections[i].modules.length; j++) {
          for (let k = 0; k < this.courseToEdit.sections[i].modules[j].classes.length; k++) {
            if (this.courseToEdit.sections[i].modules[j].classes[k].courseClassId === classId) {
              classToMove = this.courseToEdit.sections[i].modules[j].classes[k];
              fromSectionIndex = i;
              fromModuleIndex = j;
              fromClassIndex = k;
              break;
            }
          }
        }
      }

      if (!classToMove) {
        this.toast.presentErrorToast('No se encontró la clase a mover');
        return;
      }

      // Find target section and module
      const targetSection = this.courseToEdit.sections.find((s) => s.courseSectionId === sectionId);
      const targetModule = targetSection?.modules.find((m) => m.courseModuleId === moduleId);

      if (!targetSection || !targetModule) {
        this.toast.presentErrorToast('No se encontró la sección o módulo destino');
        return;
      }

      // Get target indices
      const targetSectionIndex = this.courseToEdit.sections.indexOf(targetSection);
      const targetModuleIndex = targetSection.modules.indexOf(targetModule);

      // Check if trying to move to the same location
      if (fromSectionIndex === targetSectionIndex && fromModuleIndex === targetModuleIndex) {
        // Same location, just close the form
        this.setMovingClassId(null, null, null);
        return;
      }

      // Store current attachments and expansion state before moving
      const currentAttachments = this.currentAttachments.sections[fromSectionIndex]?.modules[fromModuleIndex]?.classes[fromClassIndex]
        ? cloneDeep(this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex].classes[fromClassIndex])
        : { attachments: [] };
      const currentExpansionState = this.expandedClasses[fromSectionIndex]?.[fromModuleIndex]?.[fromClassIndex] || false;

      // Store the FormGroup before removing it
      const classFormGroup = this.getClassFormGroup(fromSectionIndex, fromModuleIndex, fromClassIndex);
      const clonedFormGroup = cloneDeep(classFormGroup.value);

      // Remove class from source location
      this.courseToEdit.sections[fromSectionIndex].modules[fromModuleIndex].classes.splice(fromClassIndex, 1);
      this.getClassesFormArray(fromSectionIndex, fromModuleIndex).removeAt(fromClassIndex);

      // Update attachments structure - remove from source
      if (this.currentAttachments.sections[fromSectionIndex]?.modules[fromModuleIndex]?.classes[fromClassIndex]) {
        this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex].classes.splice(fromClassIndex, 1);
      }

      // Update expansion arrays - remove from source
      if (this.expandedClasses[fromSectionIndex][fromModuleIndex]) {
        this.expandedClasses[fromSectionIndex][fromModuleIndex].splice(fromClassIndex, 1);
      }

      // Add class to target location
      targetModule.classes.push(classToMove);
      this.getClassesFormArray(targetSectionIndex, targetModuleIndex).push(this.createClassFormGroup(classToMove));

      // Add attachments structure for new location
      if (!this.currentAttachments.sections[targetSectionIndex]) {
        this.currentAttachments.sections[targetSectionIndex] = {
          attachments: [],
          modules: []
        };
      }
      if (!this.currentAttachments.sections[targetSectionIndex].modules[targetModuleIndex]) {
        this.currentAttachments.sections[targetSectionIndex].modules[targetModuleIndex] = {
          classes: []
        };
      }
      this.currentAttachments.sections[targetSectionIndex].modules[targetModuleIndex].classes.push({
        attachments: []
      });

      // Update expansion arrays
      this.expandedClasses[targetSectionIndex][targetModuleIndex].push(false);
      if (this.expandedClasses[fromSectionIndex][fromModuleIndex]) {
        this.expandedClasses[fromSectionIndex][fromModuleIndex].splice(fromClassIndex, 1);
      }

      // Mark form as dirty
      this.courseForm.markAsDirty();
      this.courseForm.markAsTouched();
      this.courseForm.updateValueAndValidity();

      // Save state for undo functionality
      this.lastClassMove = {
        class: cloneDeep(classToMove),
        classFormGroup: this.fb.group(clonedFormGroup),
        fromSectionIndex,
        fromModuleIndex,
        fromClassIndex,
        toSectionIndex: targetSectionIndex,
        toModuleIndex: targetModuleIndex,
        attachments: currentAttachments,
        expansionState: currentExpansionState
      };

      // Reset moving form
      this.setMovingClassId(null, null, null);

      this.toast.presentSuccessToast(
        `La clase ${classToMove.number}: ${classToMove.name} se ha movido al Modulo ${targetModule.number}: ${targetModule.name} de la Asignatura ${targetSection.number}`,
        3000,
        'Deshacer',
        '',
        () => {
          this.undoClassMove();
        }
      );
    }
  }

  undoClassMove(): void {
    if (!this.lastClassMove) {
      this.toast.presentErrorToast('No hay movimiento de clase para deshacer');
      return;
    }

    const {
      class: classToRestore,
      classFormGroup,
      fromSectionIndex,
      fromModuleIndex,
      fromClassIndex,
      toSectionIndex,
      toModuleIndex,
      attachments,
      expansionState
    } = this.lastClassMove;

    try {
      // Find the class in target location (it's at the end of the array)
      const targetClasses = this.courseToEdit.sections[toSectionIndex].modules[toModuleIndex].classes;
      const classIndexInTarget = targetClasses.length - 1;

      // Remove from target location
      this.courseToEdit.sections[toSectionIndex].modules[toModuleIndex].classes.splice(classIndexInTarget, 1);
      this.getClassesFormArray(toSectionIndex, toModuleIndex).removeAt(classIndexInTarget);

      // Remove attachments from target
      if (this.currentAttachments.sections[toSectionIndex]?.modules[toModuleIndex]?.classes[classIndexInTarget]) {
        this.currentAttachments.sections[toSectionIndex].modules[toModuleIndex].classes.splice(classIndexInTarget, 1);
      }

      // Remove expansion state from target
      if (this.expandedClasses[toSectionIndex]?.[toModuleIndex]) {
        this.expandedClasses[toSectionIndex][toModuleIndex].splice(classIndexInTarget, 1);
      }

      // Restore to original location
      this.courseToEdit.sections[fromSectionIndex].modules[fromModuleIndex].classes.splice(fromClassIndex, 0, classToRestore);
      this.getClassesFormArray(fromSectionIndex, fromModuleIndex).insert(fromClassIndex, classFormGroup);

      // Restore attachments structure
      if (!this.currentAttachments.sections[fromSectionIndex]) {
        this.currentAttachments.sections[fromSectionIndex] = {
          attachments: [],
          modules: []
        };
      }
      if (!this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex]) {
        this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex] = {
          classes: []
        };
      }
      this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex].classes.splice(fromClassIndex, 0, attachments);

      // Restore expansion state
      if (!this.expandedClasses[fromSectionIndex]) {
        this.expandedClasses[fromSectionIndex] = [];
      }
      if (!this.expandedClasses[fromSectionIndex][fromModuleIndex]) {
        this.expandedClasses[fromSectionIndex][fromModuleIndex] = [];
      }
      this.expandedClasses[fromSectionIndex][fromModuleIndex].splice(fromClassIndex, 0, expansionState);

      // Mark form as dirty
      this.courseForm.markAsDirty();
      this.courseForm.markAsTouched();
      this.courseForm.updateValueAndValidity();

      // Clear the undo state
      this.lastClassMove = null;

      // this.toast.presentSuccessToast(
      //   `Movimiento deshecho. La clase ${classToRestore.number}: ${classToRestore.name} ha vuelto al Módulo ${fromModule.number}: ${fromModule.name} de la Asignatura ${fromSection.number}`
      // );
    } catch (error) {
      console.error('Error al deshacer movimiento de clase:', error);
      this.toast.presentErrorToast('Error al deshacer el movimiento de clase');
      this.lastClassMove = null;
    }
  }

  moveModule(): void {
    // if movingModuleForm is valid move module to section
    if (this.movingModuleForm.valid) {
      const { moduleId, sectionId } = this.movingModuleForm.value;

      // Find the module to move
      let moduleToMove: ModuleToEdit | null = null;
      let fromSectionIndex = -1;
      let fromModuleIndex = -1;

      // Search for the module in the current structure
      for (let i = 0; i < this.courseToEdit.sections.length; i++) {
        for (let j = 0; j < this.courseToEdit.sections[i].modules.length; j++) {
          if (this.courseToEdit.sections[i].modules[j].courseModuleId === moduleId) {
            moduleToMove = this.courseToEdit.sections[i].modules[j];
            fromSectionIndex = i;
            fromModuleIndex = j;
            break;
          }
        }
      }

      if (!moduleToMove) {
        this.toast.presentErrorToast('No se encontró el módulo a mover');
        return;
      }

      // Find target section
      const targetSection = this.courseToEdit.sections.find((s) => s.courseSectionId === sectionId);

      if (!targetSection) {
        this.toast.presentErrorToast('No se encontró la asignatura destino');
        return;
      }

      // Check if target is the same as current location
      const targetSectionIndex = this.courseToEdit.sections.indexOf(targetSection);
      if (fromSectionIndex === targetSectionIndex) {
        this.setMovingModuleId(null, null);
        return;
      }

      // Store current attachments and expansion states before moving
      const currentAttachments = this.currentAttachments.sections[fromSectionIndex]?.modules[fromModuleIndex]
        ? cloneDeep(this.currentAttachments.sections[fromSectionIndex].modules[fromModuleIndex])
        : { classes: [] };
      const currentModuleExpanded = this.expandedModules[fromSectionIndex]?.[fromModuleIndex] || false;
      const currentClassesExpanded = this.expandedClasses[fromSectionIndex]?.[fromModuleIndex]
        ? [...this.expandedClasses[fromSectionIndex][fromModuleIndex]]
        : [];

      // Store the FormGroup before removing it
      const moduleFormGroup = this.getModuleFormGroup(fromSectionIndex, fromModuleIndex);
      const clonedFormGroup = cloneDeep(moduleFormGroup.value);

      // Remove module from source location
      this.courseToEdit.sections[fromSectionIndex].modules.splice(fromModuleIndex, 1);
      this.getModulesFormArray(fromSectionIndex).removeAt(fromModuleIndex);

      // Update attachments structure
      if (this.currentAttachments.sections[fromSectionIndex]?.modules[fromModuleIndex]) {
        this.currentAttachments.sections[fromSectionIndex].modules.splice(fromModuleIndex, 1);
      }

      // Update expansion arrays
      if (this.expandedModules[fromSectionIndex]) {
        this.expandedModules[fromSectionIndex].splice(fromModuleIndex, 1);
      }
      if (this.expandedClasses[fromSectionIndex]) {
        this.expandedClasses[fromSectionIndex].splice(fromModuleIndex, 1);
      }

      // Add module to target location
      targetSection.modules.push(moduleToMove);
      this.getModulesFormArray(targetSectionIndex).push(this.createModuleFormGroup(moduleToMove));

      // Add attachments structure for new location
      if (!this.currentAttachments.sections[targetSectionIndex]) {
        this.currentAttachments.sections[targetSectionIndex] = {
          attachments: [],
          modules: []
        };
      }
      this.currentAttachments.sections[targetSectionIndex].modules.push({
        classes: moduleToMove.classes.map(() => ({ attachments: [] }))
      });

      // Update expansion arrays for target
      if (!this.expandedModules[targetSectionIndex]) {
        this.expandedModules[targetSectionIndex] = [];
      }
      if (!this.expandedClasses[targetSectionIndex]) {
        this.expandedClasses[targetSectionIndex] = [];
      }

      this.expandedModules[targetSectionIndex].push(false);
      this.expandedClasses[targetSectionIndex].push(new Array(moduleToMove.classes.length).fill(false));

      // Mark form as dirty
      this.courseForm.markAsDirty();
      this.courseForm.markAsTouched();
      this.courseForm.updateValueAndValidity();

      // Save state for undo functionality
      this.lastModuleMove = {
        module: cloneDeep(moduleToMove),
        moduleFormGroup: this.fb.group(clonedFormGroup),
        fromSectionIndex,
        fromModuleIndex,
        toSectionIndex: targetSectionIndex,
        attachments: currentAttachments,
        expansionStates: {
          moduleExpanded: currentModuleExpanded,
          classesExpanded: currentClassesExpanded
        }
      };

      // Reset moving form
      this.setMovingModuleId(null, null);

      const fromSection = this.courseToEdit.sections[fromSectionIndex];

      this.toast.presentSuccessToast(
        `El módulo ${moduleToMove.number}: ${moduleToMove.name} se ha movido de la Asignatura ${fromSection.number}`,
        3000,
        'Deshacer',
        '',
        () => {
          this.undoModuleMove();
        }
      );
    }
  }

  undoModuleMove(): void {
    if (!this.lastModuleMove) {
      this.toast.presentErrorToast('No hay movimiento de módulo para deshacer');
      return;
    }

    const {
      module: moduleToRestore,
      moduleFormGroup,
      fromSectionIndex,
      fromModuleIndex,
      toSectionIndex,
      attachments,
      expansionStates
    } = this.lastModuleMove;

    try {
      // Find the module in target location (it's at the end of the array)
      const targetModules = this.courseToEdit.sections[toSectionIndex].modules;
      const moduleIndexInTarget = targetModules.length - 1;

      // Remove from target location
      this.courseToEdit.sections[toSectionIndex].modules.splice(moduleIndexInTarget, 1);
      this.getModulesFormArray(toSectionIndex).removeAt(moduleIndexInTarget);

      // Remove attachments from target
      if (this.currentAttachments.sections[toSectionIndex]?.modules[moduleIndexInTarget]) {
        this.currentAttachments.sections[toSectionIndex].modules.splice(moduleIndexInTarget, 1);
      }

      // Remove expansion states from target
      if (this.expandedModules[toSectionIndex]) {
        this.expandedModules[toSectionIndex].splice(moduleIndexInTarget, 1);
      }
      if (this.expandedClasses[toSectionIndex]) {
        this.expandedClasses[toSectionIndex].splice(moduleIndexInTarget, 1);
      }

      // Restore to original location
      this.courseToEdit.sections[fromSectionIndex].modules.splice(fromModuleIndex, 0, moduleToRestore);
      this.getModulesFormArray(fromSectionIndex).insert(fromModuleIndex, moduleFormGroup);

      // Restore attachments structure
      if (!this.currentAttachments.sections[fromSectionIndex]) {
        this.currentAttachments.sections[fromSectionIndex] = {
          attachments: [],
          modules: []
        };
      }
      this.currentAttachments.sections[fromSectionIndex].modules.splice(fromModuleIndex, 0, attachments);

      // Restore expansion states
      if (!this.expandedModules[fromSectionIndex]) {
        this.expandedModules[fromSectionIndex] = [];
      }
      if (!this.expandedClasses[fromSectionIndex]) {
        this.expandedClasses[fromSectionIndex] = [];
      }
      this.expandedModules[fromSectionIndex].splice(fromModuleIndex, 0, expansionStates.moduleExpanded);
      this.expandedClasses[fromSectionIndex].splice(fromModuleIndex, 0, expansionStates.classesExpanded);

      // Mark form as dirty
      this.courseForm.markAsDirty();
      this.courseForm.markAsTouched();
      this.courseForm.updateValueAndValidity();

      // Clear the undo state
      this.lastModuleMove = null;

      // this.toast.presentSuccessToast(
      //   `Movimiento deshecho. El módulo ${moduleToRestore.number}: ${moduleToRestore.name} ha vuelto a la Asignatura ${fromSection.number}: ${fromSection.name}`
      // );
    } catch (error) {
      console.error('Error al deshacer movimiento de módulo:', error);
      this.toast.presentErrorToast('Error al deshacer el movimiento de módulo');
      this.lastModuleMove = null;
    }
  }

  /**
   * Verifica si una clase puede ser movida.
   * Una clase solo puede moverse si hay más de una clase en el módulo.
   */
  canMoveClass(sectionIndex: number, moduleIndex: number): boolean {
    if (!this.courseToEdit?.sections?.[sectionIndex]?.modules?.[moduleIndex]) {
      return false;
    }
    const classesCount = this.courseToEdit.sections[sectionIndex].modules[moduleIndex].classes.length;
    return classesCount > 1;
  }

  /**
   * Verifica si un módulo puede ser movido.
   * Un módulo solo puede moverse si hay más de un módulo en la sección.
   */
  canMoveModule(sectionIndex: number): boolean {
    if (!this.courseToEdit?.sections?.[sectionIndex]) {
      return false;
    }
    const modulesCount = this.courseToEdit.sections[sectionIndex].modules.length;
    return modulesCount > 1;
  }
}
